import React, { useState, useEffect, useRef, useMemo, useDeferredValue } from 'react';
import {
  CalendarBlank, ArrowClockwise, TrendUp, Users, ChartBar, Pulse, UploadSimple, CurrencyDollar, Wallet,
  UserCheck, DownloadSimple, CaretDown, CaretRight, EnvelopeSimple, PaperPlaneTilt, MagnifyingGlass,
  VideoCamera, Trash, SquaresFour, Paperclip, ArrowRight, WarningCircle, CheckCircle, Warning,
  ClipboardText, GearSix, ChartPieSlice,
} from '@phosphor-icons/react';
import { motion, AnimatePresence, MotionConfig } from 'motion/react';
import PostReuniaoTab from './PostReuniaoTab';

// Estado de carregamento: skeleton na forma final (cartões de KPI + bloco de gráfico/lista),
// com o brilho .skel do kit do CareOS (para sozinho com prefers-reduced-motion).
const TabSkeleton = ({ label = 'Carregando...' }) => (
  <div className="py-8 space-y-4" role="status" aria-busy="true" aria-live="polite">
    <span className="sr-only">{label}</span>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="ui-card h-24" data-densidade="compacta">
          <div className="skel h-3 w-1/2" />
          <div className="skel h-6 w-2/3" />
        </div>
      ))}
    </div>
    <div className="ui-card h-64" aria-hidden="true">
      <div className="skel h-4 w-1/3" />
      <div className="skel h-40 w-full" />
    </div>
  </div>
);

// Números de KPI: valor final direto. A contagem de 0 até o valor durava 0,8 s
// (quase 3x o degrau mais lento do DS), só decorava e ignorava "reduzir movimento"
// (DS v3 §5: motion orienta, nunca decora). Os nomes ficam para não mexer nos usos.
const AnimNum = ({ value, suffix = '' }) => (
  <span>{`${Math.round(Number(value) || 0).toLocaleString('pt-BR')}${suffix}`}</span>
);

const AnimDecimal = ({ value, suffix = '%' }) => (
  <span>{`${(Number(value) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}${suffix}`}</span>
);
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import authedFetch from '../lib/authedFetch';
import { safeUrl } from '../lib/safeUrl';
import lazyWithRetry from '../lib/lazyWithRetry';
import ErrorBoundary from '../components/ErrorBoundary';
import BarraLateral from '../components/BarraLateral';
// lazyWithRetry: depois de um deploy o pedaço de JS antigo some; recarrega a página uma vez.
const PipelineTab = lazyWithRetry(() => import('./PipelineTab'));
const ReceitasTab = lazyWithRetry(() => import('./ReceitasTab'));
const ReunioesTab = lazyWithRetry(() => import('./ReunioesTab'));
const AdminTab = lazyWithRetry(() => import('./AdminTab'));
const ProjetosTab = lazyWithRetry(() => import('./ProjetosTab'));
const VisaoConsolidadaTab = lazyWithRetry(() => import('./VisaoConsolidadaTab'));

// Formatadores de data criados UMA vez (criar Intl.DateTimeFormat por chamada custava
// 40–210 µs; com centenas de leads, dezenas a centenas de ms por render). Sempre em BRT.
const TZ_BRT = 'America/Sao_Paulo';
const FMT_YMD_BRT = new Intl.DateTimeFormat('en-CA', { timeZone: TZ_BRT, year: 'numeric', month: '2-digit', day: '2-digit' });
const FMT_DM_BRT = new Intl.DateTimeFormat('pt-BR', { timeZone: TZ_BRT, day: '2-digit', month: '2-digit' });
const FMT_HM_BRT = new Intl.DateTimeFormat('pt-BR', { timeZone: TZ_BRT, hour: '2-digit', minute: '2-digit' });

// Semana ISO (segunda→domingo) e mês, ancorados em BRT — mesma régua do backend.
// Data inválida devolve null (Intl.format lança RangeError, então a guarda vem antes).
const isoWeekKeyBRT = (ts) => {
  const d = new Date(ts);
  if (isNaN(d)) return null;
  const ymd = FMT_YMD_BRT.format(d);
  const [yy, mm, dd] = ymd.split('-').map(Number);
  const t = new Date(yy, mm - 1, dd);
  const dayNr = (t.getDay() + 6) % 7;       // segunda = 0
  t.setDate(t.getDate() - dayNr + 3);        // quinta-feira da semana ISO
  const isoYear = t.getFullYear();
  const jan4 = new Date(isoYear, 0, 4);
  const j4 = (jan4.getDay() + 6) % 7;
  const firstThu = new Date(jan4);
  firstThu.setDate(jan4.getDate() - j4 + 3);
  const week = 1 + Math.round((t - firstThu) / (7 * 24 * 3600 * 1000));
  return `${isoYear}-W${String(week).padStart(2, '0')}`;
};
const monthKeyBRT = (ts) => {
  const d = new Date(ts);
  if (isNaN(d)) return null;
  return FMT_YMD_BRT.format(d).slice(0, 7);
};
// Bucketiza leads filtrados → {week|month, leads_total, count, reunioes_organico, reunioes_anuncio}.
const bucketLeads = (leads, keyFn, keyName) => {
  const map = {};
  for (const l of leads) {
    if (!l.submitted_at) continue;
    const key = keyFn(l.submitted_at);
    if (!key) continue;
    const b = map[key] || (map[key] = { [keyName]: key, leads_total: 0, count: 0, reunioes_organico: 0, reunioes_anuncio: 0 });
    b.leads_total += 1;
    b.count += 1;
    if (l.reuniao_agendada) {
      if ((l.origem || 'Orgânico') === 'Anúncio') b.reunioes_anuncio += 1;
      else b.reunioes_organico += 1;
    }
  }
  return Object.values(map);
};
// Formata data e hora no formato "03/02 às 13:26" (fuso de Brasília, independente do navegador).
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date)) return '-';
  return `${FMT_DM_BRT.format(date)} às ${FMT_HM_BRT.format(date)}`;
};

// Tipos de lead buscados na abertura (uma onda só) e a rota de cada um.
const ROTAS_LEADS = {
  consultoria: '/api/typeform/leads/consultoria',
  curso: '/api/typeform/leads/curso',
  aplicacao: '/api/typeform/leads/aplicacao',
  suica: '/api/typeform/leads/suica',
  consorcio: '/api/typeform/leads/consorcio',
  'anuncio-meta': '/api/leads/anuncio-form-meta/db',
};

const DISTRIBUTION_BAR_COLORS = {
  renda: 'var(--care-chart-cat-6)',
  patrimonio: 'var(--care-chart-cat-3)',
  aporte: 'var(--care-chart-cat-2)',
};

// Quem é admin do painel é decidido pelo SERVIDOR (PAINEL_ADMIN_EMAILS), que
// devolve `is_painel_admin` no login. Esta lista é só o fallback para sessões
// abertas antes disso existir — ela estava dessincronizada do backend
// (ic@caremfo.com era admin lá e não aqui, e via a tela em somente-leitura).
const ADMIN_EMAILS_FALLBACK = new Set(['mk@caremfo.com','jv@caremfo.com','cl@caremfo.com','mh@caremfo.com']);
const ehAdminPainel = (u) => (typeof u?.is_painel_admin === 'boolean'
  ? u.is_painel_admin
  : ADMIN_EMAILS_FALLBACK.has((u?.email || '').toLowerCase()));

// Teclado das abas (padrão ARIA tabs): setas, Home e End movem o foco entre as
// abas; Enter/Espaço ativam pelo próprio clique do botão. Só mexe no foco.
const moverFocoAba = (e) => {
  if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
  const abas = Array.from(e.currentTarget.querySelectorAll('[role="tab"]'));
  const i = abas.indexOf(document.activeElement);
  if (i < 0) return;
  e.preventDefault();
  const n = abas.length;
  const j = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
  abas[j].focus();
};
// Tabindex móvel: só a aba ativa entra no Tab (se nenhuma da lista estiver ativa, a primeira).
const tabIndexAba = (lista, atual, id) => (
  atual === id || (!lista.some((a) => a.id === atual) && lista[0]?.id === id) ? 0 : -1
);

const NOME_TIPO_LEAD = {
  consultoria: 'Consultoria', curso: 'Curso', aplicacao: 'Aplicação',
  suica: 'Suíça', consorcio: 'Consórcio', 'anuncio-meta': 'Anúncio Meta',
};

// Checkbox nativo: alvo no label (min 32px), desabilitado com a opacidade do DS.
const CLS_CHECKBOX = 'w-4 h-4 accent-[color:var(--care-accent-solid)] cursor-pointer disabled:cursor-not-allowed disabled:opacity-[var(--care-state-disabled-opacity)]';
const CLS_LABEL_CHECK = 'inline-flex items-center gap-2 min-h-[32px] px-1 text-xs text-[color:var(--care-text-secondary)] cursor-pointer';
// Linha de lead que não usa .ui-linha (bloco de várias linhas no celular): mesmos estados do kit.
const CLS_LINHA_BLOCO = 'px-2 py-3 rounded-lg cursor-pointer transition-colors hover:bg-[var(--secondary)] active:bg-[var(--care-state-pressed-overlay)]';
// Rótulo numérico sobre a barra: cor de rótulo de gráfico, a posição já identifica a série.
const CLS_ROTULO_BARRA = 'text-[11px] font-medium text-[color:var(--care-chart-label)] tabular-nums lining-nums mb-0.5';
// .ui-fato empilhado (rótulo em cima, valor embaixo) para "Todas as Respostas".
const FATO_PILHA = { display: 'flex', flexDirection: 'column', alignItems: 'flex-start' };

// Ícone do botão de e-mail ao consultor (Phosphor bold em 16px, currentColor).
const IconeEnvio = ({ enviando, enviado }) => (enviando
  ? <ArrowClockwise size={16} weight="bold" aria-hidden="true" className="motion-safe:animate-spin" />
  : enviado
    ? <EnvelopeSimple size={16} weight="bold" aria-hidden="true" />
    : <PaperPlaneTilt size={16} weight="bold" aria-hidden="true" />);

// Gatilho de expandir/recolher a linha do lead (teclado + leitor de tela).
// A linha inteira continua clicável com o mouse; este botão é o caminho acessível.
const BotaoExpandir = ({ aberto, controla, onAlternar, className }) => (
  <button
    type="button"
    className={`ui-botao${className ? ` ${className}` : ''}`}
    data-variante="fantasma"
    data-tamanho="sm"
    data-so-icone
    aria-expanded={aberto}
    aria-controls={controla}
    aria-label={aberto ? 'Recolher detalhes' : 'Ver detalhes'}
    onClick={(e) => { e.stopPropagation(); onAlternar(); }}
  >
    <CaretDown size={20} aria-hidden="true" className={`transition-transform duration-fast ease-care ${aberto ? 'rotate-180' : ''}`} />
  </button>
);

const TypeformDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  // Nasce no ano corrente: a abertura busca tudo numa onda só, sem rodada prévia
  // para descobrir os anos. Janeiro (ano ainda sem leads) troca uma vez, abaixo.
  const [selectedYear, setSelectedYear] = useState(() => new Date().getFullYear());
  // true depois da primeira resposta válida (ou de o usuário escolher um ano):
  // a troca automática para o ano mais recente com leads acontece no máximo uma vez.
  const anoDecididoRef = useRef(false);
  // Contadores de rodada: resposta de uma rodada antiga (ano trocado no meio) é descartada.
  const leadsReqRef = useRef(0);
  const distribReqRef = useRef(0);
  const captacaoReqRef = useRef(0);
  // AUC e captação só são buscados na primeira entrada na aba AUC (pl).
  const plCarregadoRef = useRef(false);
  const [selectedType, setSelectedType] = useState('aplicacao');
  const [consultoriaData, setConsultoriaData] = useState(null);
  const [cursoData, setCursoData] = useState(null);
  const [aplicacaoData, setAplicacaoData] = useState(null);
  const [suicaData, setSuicaData] = useState(null);
  const [consorcioData, setConsorcioData] = useState(null);
  const [anuncioMetaData, setAnuncioMetaData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [expandedLeads, setExpandedLeads] = useState(new Set());
  const [aplicacaoFilter, setAplicacaoFilter] = useState('qualificados'); // 'qualificados', 'desqualificados', 'todos'
  const [origemFilter, setOrigemFilter] = useState('todos'); // 'todos', 'organico', 'anuncio'
  const [reuniaoFilter, setReuniaoFilter] = useState('todos'); // 'todos', 'sem_reuniao'
  const [consultorTagFilter, setConsultorTagFilter] = useState('todos'); // 'todos', 'CL', 'MH'
  const [consultoriaFilter, setConsultoriaFilter] = useState('todos'); // 'todos', 'aprovados', 'nao_aprovados'
  const [cursoFilter, setCursoFilter] = useState('todos'); // 'todos'
  const [suicaFilter, setSuicaFilter] = useState('todos'); // 'todos', 'qualificados', 'nao_qualificados'
  const [searchTerm, setSearchTerm] = useState('');
  // A digitação atualiza o campo na hora; filtro, gráfico e lista seguem um passo atrás.
  const searchDeferido = useDeferredValue(searchTerm);
  // Erro do fetch principal da aba Leads (espelha o padrão do aucError)
  const [leadsError, setLeadsError] = useState(false);
  // Tipos de lead cuja chamada falhou (falha parcial): avisa acima dos KPIs daquele tipo.
  const [leadsFalhas, setLeadsFalhas] = useState([]);

  // Lista dinâmica de consultores (puxa de /api/consultores-ativos — público)
  const [consultoresAtivos, setConsultoresAtivos] = useState([]);
  // Endpoint público (sem admin_guard) — evita 401 silencioso quando o
  // componente monta antes do AuthContext popular o token MSAL.
  useEffect(() => {
    (async () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/consultores-ativos`;
        const r = await fetch(url);
        if (r.ok) {
          const d = await r.json();
          setConsultoresAtivos(d.consultores || []);
        }
      } catch (e) {
        console.warn('Falha ao buscar consultores ativos:', e);
      }
    })();
  }, []);
  // Display: mostra o nome cadastrado no Admin, sem heurística.
  const consultorDisplay = (c) => c.nome;
  const renderConsultorOptions = (placeholder = 'Selecionar') => (
    <>
      <option value="">{placeholder}</option>
      {consultoresAtivos.map(c => (
        <option key={c.id} value={c.nome} title={c.nome}>{consultorDisplay(c)}</option>
      ))}
    </>
  );

  // Distribuição de respostas state
  const [distribuicaoData, setDistribuicaoData] = useState(null);
  
  // AUC state
  const [activeTab, setActiveTab] = useState('leads'); // 'leads' or 'pl'
  const [aucData, setAucData] = useState(null);
  const [aucLoading, setAucLoading] = useState(false);
  const [aucError, setAucError] = useState(false);
  // Captação (STVM + posições) — seletor mensal/anual
  const [captacao, setCaptacao] = useState({ periodos: [], consultores: [], totais: {} });
  const [captacaoErro, setCaptacaoErro] = useState(false);
  const [captacaoView, setCaptacaoView] = useState('mensal'); // 'mensal' | 'anual'
  const [captacaoAno, setCaptacaoAno] = useState(new Date().getFullYear());
  const [captacaoAnos, setCaptacaoAnos] = useState([]);
  const [uploadingBtg, setUploadingBtg] = useState(false);
  const [uploadingXp, setUploadingXp] = useState(false);
  const [uploadMsgBtg, setUploadMsgBtg] = useState(null);
  const [uploadMsgXp, setUploadMsgXp] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Helper function to get correct field value based on form source
  const getLeadFieldValue = (lead, fieldType) => {
    const fieldIds = {
      // Formulário 1 (Original - Orgânico)
      form1: {
        renda: 'Fk6qaklsLGZ0',
        patrimonio: 'qXPBLW9bEORW',
        aporte: 'hHwv1DkikHRZ'
      },
      // Formulário 2 (Novo - Anúncio)
      form2: {
        renda: 'XH1Umy3DekmV',
        patrimonio: 'Sxs1t8TRK79r',
        aporte: 'm653FRTqdkXF'
      }
    };

    const isForm2 = lead.form_source === 'Formulário 2';
    const ids = isForm2 ? fieldIds.form2 : fieldIds.form1;
    
    // Try the specific form's field ID first, then fallback to the other form
    const primaryId = ids[fieldType];
    const fallbackId = isForm2 ? fieldIds.form1[fieldType] : fieldIds.form2[fieldType];
    
    return lead.respostas?.[primaryId] || lead.respostas?.[fallbackId] || '-';
  };

  // Abertura / troca de ano / Atualizar: as 6 rotas do ano numa onda só. Cada resposta
  // já traz `available_years` (anos de TODOS os leads, sem filtro), então não há rodada
  // prévia para descobrir os anos. Os corpos são lidos todos antes de qualquer setState:
  // um render por rodada. Resposta de rodada antiga (ano trocado no meio) é descartada.
  const fetchAllData = async () => {
    const ano = selectedYear;
    const req = ++leadsReqRef.current;
    let trocouAno = false;
    setLoading(true);
    setLeadsError(false);
    setLeadsFalhas([]);
    try {
      const tipos = Object.keys(ROTAS_LEADS);
      const respostas = await Promise.all(tipos.map((t) => (
        authedFetch(`${backendUrl}${ROTAS_LEADS[t]}?year=${ano}`).catch((err) => {
          console.error(`Error fetching ${t}:`, err);
          return null;
        })
      )));
      const corpos = await Promise.all(respostas.map((r) => (r && r.ok ? r.json().catch(() => null) : null)));
      if (req !== leadsReqRef.current) return; // rodada antiga: descarta

      const porTipo = {};
      tipos.forEach((t, i) => { porTipo[t] = corpos[i]; });

      // Anos: união dos available_years de todas as respostas que vieram.
      const anos = new Set();
      corpos.forEach((c) => { (c?.available_years || []).forEach((y) => anos.add(y)); });
      const anosOrdenados = Array.from(anos).sort((a, b) => b - a);
      if (anosOrdenados.length) setAvailableYears(anosOrdenados);

      // Janeiro: o ano corrente ainda não tem leads. Uma única vez, vai para o ano
      // mais recente com leads (o efeito de selectedYear dispara a nova rodada).
      if (!anoDecididoRef.current && corpos.some(Boolean)) {
        anoDecididoRef.current = true;
        if (anosOrdenados.length && !anosOrdenados.includes(ano)) {
          trocouAno = true;
          setSelectedYear(anosOrdenados[0]);
          return;
        }
      }

      if (porTipo.consultoria) setConsultoriaData(porTipo.consultoria);
      if (porTipo.curso) setCursoData(porTipo.curso);
      if (porTipo.aplicacao) setAplicacaoData(porTipo.aplicacao);
      if (porTipo.suica) setSuicaData(porTipo.suica);
      if (porTipo.consorcio) setConsorcioData(porTipo.consorcio);
      if (porTipo['anuncio-meta']) setAnuncioMetaData(porTipo['anuncio-meta']);

      // Falha parcial: guarda quais tipos não vieram (só para o aviso na tela).
      setLeadsFalhas(tipos.filter((t) => !porTipo[t]));

      // Se nenhuma resposta principal veio ok, a página mostraria zeros — sinaliza erro.
      if (!corpos.some(Boolean)) {
        setLeadsError(true);
      }

      setLastUpdate(new Date());
    } catch (error) {
      if (req !== leadsReqRef.current) return;
      console.error('Error fetching data:', error);
      setLeadsError(true);
    } finally {
      // Só a rodada mais recente apaga o "carregando"; na troca automática de ano
      // a próxima rodada já vai religá-lo, então não pisca.
      if (req === leadsReqRef.current && !trocouAno) setLoading(false);
    }
  };

  // Sync do Sheets só mexe na Aplicação: refaz apenas /aplicacao do ano na tela.
  const fetchAplicacao = async () => {
    const ano = selectedYear;
    const reqLeads = leadsReqRef.current;
    try {
      const res = await authedFetch(`${backendUrl}${ROTAS_LEADS.aplicacao}?year=${ano}`);
      const data = res.ok ? await res.json().catch(() => null) : null;
      // Se uma rodada completa começou depois (troca de ano, Atualizar), ela manda.
      if (reqLeads !== leadsReqRef.current || !data) return;
      setAplicacaoData(data);
      setLeadsFalhas((f) => f.filter((t) => t !== 'aplicacao'));
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Erro ao recarregar a Aplicação:', err);
    }
  };

  const fetchAucData = async () => {
    // Só mostra o spinner no primeiro load; refresh em background não pisca a tela.
    if (!aucData) setAucLoading(true);
    setAucError(false);
    try {
      const res = await authedFetch(`${backendUrl}/api/auc/consolidated`);
      if (res.ok) {
        setAucData(await res.json());
      } else {
        setAucError(true);
      }
    } catch (error) {
      console.error('Error fetching AUC data:', error);
      setAucError(true);
    } finally {
      setAucLoading(false);
    }
  };

  // Captação (STVM + posições) — substitui o NNM: modelo sem dupla contagem
  const fetchCaptacao = async () => {
    // Troca rápida de visão/ano: só a resposta do último pedido vale.
    const req = ++captacaoReqRef.current;
    try {
      const qs = captacaoView === 'anual'
        ? 'view=anual'
        : `view=mensal&year=${captacaoAno}`;
      const res = await authedFetch(`${backendUrl}/api/pipeline/captacao?${qs}`);
      if (req !== captacaoReqRef.current) return;
      if (res.ok) {
        const d = await res.json();
        if (req !== captacaoReqRef.current) return;
        // O backend devolve {"indisponivel": true} quando a fonte falha — não mostrar R$ 0
        if (d.indisponivel) {
          setCaptacaoErro(true);
          return;
        }
        setCaptacaoErro(false);
        setCaptacao({
          periodos: d.periodos || [],
          consultores: d.consultores || [],
          totais: d.totais || {},
        });
      } else {
        setCaptacaoErro(true);
      }
    } catch (err) {
      if (req !== captacaoReqRef.current) return;
      console.error('Erro ao buscar captação:', err);
      setCaptacaoErro(true);
    }
  };

  const fetchCaptacaoAnos = async () => {
    try {
      const res = await authedFetch(`${backendUrl}/api/pipeline/captacao/anos`);
      if (res.ok) {
        const d = await res.json();
        if (d.anos?.length) {
          setCaptacaoAnos(d.anos);
          setCaptacaoAno((a) => (d.anos.includes(a) ? a : d.anos[0]));
        }
      }
    } catch (err) {
      console.error('Erro ao buscar anos de captação:', err);
    }
  };

  const handleUploadBtg = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingBtg(true);
    setUploadMsgBtg(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await authedFetch(`${backendUrl}/api/pl-btg/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (res.ok) {
        setUploadMsgBtg({ type: 'success', text: `BTG ${data.action === 'updated' ? 'atualizado' : 'adicionado'}: ${data.ref_month}` });
        fetchAucData();
      } else {
        setUploadMsgBtg({ type: 'error', text: data.detail || 'Erro ao processar' });
      }
    } catch (error) {
      setUploadMsgBtg({ type: 'error', text: 'Erro ao fazer upload' });
    } finally {
      setUploadingBtg(false);
      e.target.value = '';
    }
  };

  const handleUploadXp = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingXp(true);
    setUploadMsgXp(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await authedFetch(`${backendUrl}/api/pl-xp/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (res.ok) {
        setUploadMsgXp({ type: 'success', text: `XP ${data.action === 'updated' ? 'atualizado' : 'adicionado'}: ${data.ref_month}` });
        fetchAucData();
      } else {
        setUploadMsgXp({ type: 'error', text: data.detail || 'Erro ao processar' });
      }
    } catch (error) {
      setUploadMsgXp({ type: 'error', text: 'Erro ao fazer upload' });
    } finally {
      setUploadingXp(false);
      e.target.value = '';
    }
  };

  const toggleLead = (responseId) => {
    const newExpanded = new Set(expandedLeads);
    if (newExpanded.has(responseId)) {
      newExpanded.delete(responseId);
    } else {
      newExpanded.add(responseId);
    }
    setExpandedLeads(newExpanded);
  };

  // Helper: atualiza um lead localmente no state sem recarregar tudo
  const atualizarLeadLocal = (responseId, campos) => {
    if (!aplicacaoData) return;
    setAplicacaoData(prev => ({
      ...prev,
      leads: prev.leads.map(l => 
        l.response_id === responseId ? { ...l, ...campos } : l
      )
    }));
  };

  const marcarContato = async (responseId, contatoRealizado) => {
    atualizarLeadLocal(responseId, { contato_realizado: contatoRealizado });
    try {
      const res = await authedFetch(`${backendUrl}/api/typeform/leads/aplicacao/${responseId}/contato?contato_realizado=${contatoRealizado}`, {
        method: 'PUT'
      });
      if (!res.ok) atualizarLeadLocal(responseId, { contato_realizado: !contatoRealizado });
    } catch (err) {
      console.error('Erro:', err);
      atualizarLeadLocal(responseId, { contato_realizado: !contatoRealizado });
    }
  };

  const marcarReuniaoAgendada = async (responseId, reuniaoAgendada) => {
    atualizarLeadLocal(responseId, { reuniao_agendada: reuniaoAgendada });
    try {
      const res = await authedFetch(`${backendUrl}/api/typeform/leads/aplicacao/${responseId}/reuniao-agendada?reuniao_agendada=${reuniaoAgendada}`, {
        method: 'PUT'
      });
      if (!res.ok) atualizarLeadLocal(responseId, { reuniao_agendada: !reuniaoAgendada });
    } catch (err) {
      console.error('Erro:', err);
      atualizarLeadLocal(responseId, { reuniao_agendada: !reuniaoAgendada });
    }
  };

  const marcarReuniao = async (responseId, reuniaoRealizada) => {
    atualizarLeadLocal(responseId, { reuniao_realizada: reuniaoRealizada });
    try {
      const res = await authedFetch(`${backendUrl}/api/typeform/leads/aplicacao/${responseId}/reuniao?reuniao_realizada=${reuniaoRealizada}`, {
        method: 'PUT'
      });
      if (!res.ok) atualizarLeadLocal(responseId, { reuniao_realizada: !reuniaoRealizada });
    } catch (err) {
      console.error('Erro:', err);
      atualizarLeadLocal(responseId, { reuniao_realizada: !reuniaoRealizada });
    }
  };

  const atualizarConsultor = async (responseId, consultor) => {
    // Erro: desfaz só este lead (antes recarregava o painel inteiro).
    const anterior = (aplicacaoData?.leads || []).find((l) => l.response_id === responseId)?.consultor_responsavel;
    const desfazer = () => atualizarLeadLocal(responseId, { consultor_responsavel: anterior });
    atualizarLeadLocal(responseId, { consultor_responsavel: consultor });
    try {
      const res = await authedFetch(`${backendUrl}/api/typeform/leads/aplicacao/${responseId}/consultor?consultor=${encodeURIComponent(consultor)}`, {
        method: 'PUT'
      });
      if (!res.ok) desfazer();
    } catch (err) {
      console.error('Erro:', err);
      desfazer();
    }
  };

  const [sendingEmail, setSendingEmail] = useState({});
  const [emailMessage, setEmailMessage] = useState(null);

  const [savingObs, setSavingObs] = useState({});
  // Falha ao salvar a observação, por lead (antes só ia para o console: falha silenciosa).
  const [erroObs, setErroObs] = useState({});
  const [leadToDelete, setLeadToDelete] = useState(null);  // lead aguardando confirmação de exclusão
  const [deletingLead, setDeletingLead] = useState(false);
  // Botão que abriu o modal de apagar: o foco volta para ele quando o modal fecha.
  const gatilhoApagarRef = useRef(null);
  useEffect(() => {
    if (leadToDelete || !gatilhoApagarRef.current) return;
    const el = gatilhoApagarRef.current;
    gatilhoApagarRef.current = null;
    if (document.contains(el)) el.focus();
  }, [leadToDelete]);
  const abrirApagarLead = (e, lead) => {
    gatilhoApagarRef.current = e.currentTarget;
    setLeadToDelete(lead);
  };

  const apagarLead = async (lead) => {
    if (!lead?.response_id) return;
    setDeletingLead(true);
    try {
      const res = await authedFetch(`${backendUrl}/api/typeform/leads/aplicacao/${lead.response_id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Erro ${res.status}`);
      setAplicacaoData(prev => prev ? { ...prev, leads: (prev.leads || []).filter(l => l.response_id !== lead.response_id) } : prev);
      setLeadToDelete(null);
    } catch (e) {
      alert('Não consegui apagar o lead: ' + (e.message || 'erro'));
    } finally {
      setDeletingLead(false);
    }
  };

  const salvarObservacao = async (responseId, observacao) => {
    setSavingObs(prev => ({ ...prev, [responseId]: true }));
    setErroObs(prev => ({ ...prev, [responseId]: false }));
    try {
      const res = await authedFetch(`${backendUrl}/api/typeform/leads/aplicacao/${responseId}/observacao`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ observacao })
      });
      if (res.ok) {
        atualizarLeadLocal(responseId, { observacao });
      } else {
        setErroObs(prev => ({ ...prev, [responseId]: true }));
      }
    } catch (err) {
      console.error('Erro ao salvar observação:', err);
      setErroObs(prev => ({ ...prev, [responseId]: true }));
    } finally {
      setSavingObs(prev => ({ ...prev, [responseId]: false }));
    }
  };

  const enviarEmailConsultor = async (responseId, consultor) => {
    if (!consultor) {
      setEmailMessage({ type: 'error', text: 'Selecione um consultor primeiro' });
      setTimeout(() => setEmailMessage(null), 3000);
      return;
    }

    setSendingEmail(prev => ({ ...prev, [responseId]: true }));
    setEmailMessage(null);

    try {
      const res = await authedFetch(`${backendUrl}/api/typeform/leads/aplicacao/${responseId}/enviar-email?consultor=${encodeURIComponent(consultor)}`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setEmailMessage({ type: 'success', text: `E-mail enviado para ${consultor}!` });
        atualizarLeadLocal(responseId, { contato_realizado: true });
      } else {
        setEmailMessage({ type: 'error', text: data.detail || 'Erro ao enviar e-mail' });
      }
    } catch (err) {
      console.error('Erro:', err);
      setEmailMessage({ type: 'error', text: 'Erro de conexão ao enviar e-mail' });
    } finally {
      setSendingEmail(prev => ({ ...prev, [responseId]: false }));
      setTimeout(() => setEmailMessage(null), 4000);
    }
  };

  const [syncingSheets, setSyncingSheets] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);

  // Funções para Anúncio Meta
  const atualizarLeadAnuncioMeta = (responseId, updates) => {
    setAnuncioMetaData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        leads: prev.leads.map(lead => 
          lead.response_id === responseId ? { ...lead, ...updates } : lead
        )
      };
    });
  };

  const marcarContatoAnuncioMeta = async (responseId, contatoRealizado) => {
    atualizarLeadAnuncioMeta(responseId, { contato_realizado: contatoRealizado });
    try {
      const res = await authedFetch(`${backendUrl}/api/leads/anuncio-form-meta/${responseId}/contato?contato_realizado=${contatoRealizado}`, {
        method: 'PUT'
      });
      if (!res.ok) atualizarLeadAnuncioMeta(responseId, { contato_realizado: !contatoRealizado });
    } catch (err) {
      console.error('Erro:', err);
      atualizarLeadAnuncioMeta(responseId, { contato_realizado: !contatoRealizado });
    }
  };

  const marcarReuniaoAnuncioMeta = async (responseId, reuniaoRealizada) => {
    atualizarLeadAnuncioMeta(responseId, { reuniao_realizada: reuniaoRealizada });
    try {
      const res = await authedFetch(`${backendUrl}/api/leads/anuncio-form-meta/${responseId}/reuniao?reuniao_realizada=${reuniaoRealizada}`, {
        method: 'PUT'
      });
      if (!res.ok) atualizarLeadAnuncioMeta(responseId, { reuniao_realizada: !reuniaoRealizada });
    } catch (err) {
      console.error('Erro:', err);
      atualizarLeadAnuncioMeta(responseId, { reuniao_realizada: !reuniaoRealizada });
    }
  };

  const atualizarConsultorAnuncioMeta = async (responseId, consultor) => {
    atualizarLeadAnuncioMeta(responseId, { consultor_responsavel: consultor });
    try {
      await authedFetch(`${backendUrl}/api/leads/anuncio-form-meta/${responseId}/consultor?consultor=${encodeURIComponent(consultor)}`, {
        method: 'PUT'
      });
    } catch (err) {
      console.error('Erro:', err);
    }
  };

  const enviarEmailConsultorAnuncioMeta = async (responseId, consultor) => {
    if (!consultor || consultor === 'Consultor') return;
    
    setSendingEmail(prev => ({ ...prev, [responseId]: true }));
    try {
      const res = await authedFetch(`${backendUrl}/api/leads/anuncio-form-meta/${responseId}/enviar-email?consultor=${encodeURIComponent(consultor)}`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setEmailMessage({ type: 'success', text: `E-mail enviado para ${consultor}!` });
        atualizarLeadAnuncioMeta(responseId, { contato_realizado: true, consultor_responsavel: consultor });
      } else {
        const errMsg = data.detail || data.error || 'Erro ao enviar e-mail';
        setEmailMessage({ type: 'error', text: errMsg });
      }
      setTimeout(() => setEmailMessage(null), 4000);
    } catch (err) {
      console.error('Erro:', err);
      setEmailMessage({ type: 'error', text: 'Erro ao enviar e-mail' });
      setTimeout(() => setEmailMessage(null), 4000);
    } finally {
      setSendingEmail(prev => ({ ...prev, [responseId]: false }));
    }
  };

  const syncGoogleSheets = async () => {
    setSyncingSheets(true);
    setSyncMessage(null);
    // Guarda o tipo numa variável local — ler syncMessage no finally seria state stale
    let msgType = 'error';

    try {
      const res = await authedFetch(`${backendUrl}/api/typeform/leads/aplicacao/sync-google-sheets`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (res.ok) {
        let msg = `Sincronizado! ${data.novos} novos leads, ${data.atualizados} atualizados. (${data.qualificados} qualificados)`;
        let type = 'success';
        
        if (data.alertas_estrutura && data.alertas_estrutura.length > 0) {
          msg += ` | ALERTA: Estrutura da planilha mudou: ${data.alertas_estrutura.join('; ')}`;
          type = 'warning';
        }

        msgType = type;
        setSyncMessage({ type, text: msg });
        fetchAplicacao();
      } else {
        setSyncMessage({ type: 'error', text: data.detail || 'Erro na sincronização' });
      }
    } catch (err) {
      console.error('Erro:', err);
      setSyncMessage({ type: 'error', text: 'Erro de conexão' });
    } finally {
      setSyncingSheets(false);
      setTimeout(() => setSyncMessage(null), msgType === 'warning' ? 15000 : 5000);
    }
  };

  const downloadExcel = async () => {
    try {
      const res = await authedFetch(`${backendUrl}/api/typeform/leads/aplicacao/download-qualificados`);
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Data em BRT (toISOString é UTC e vira o dia seguinte depois das 21h)
        a.download = `leads_aplicacao_${FMT_YMD_BRT.format(new Date())}.xlsx`;
        a.click();
      }
    } catch (err) {
      console.error('Erro:', err);
    }
  };

  const fetchDistribuicao = async (year) => {
    const req = ++distribReqRef.current;
    try {
      const res = await authedFetch(`${backendUrl}/api/typeform/leads/aplicacao-distribuicao${year ? `?year=${year}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        if (req !== distribReqRef.current) return; // ano trocado no meio: descarta
        setDistribuicaoData(data);
      }
    } catch (err) {
      console.error('Erro ao buscar distribuição:', err);
    }
  };

  // Uma onda por ano: leads (6 rotas) + distribuição. AUC e captação não dependem
  // do ano e só carregam ao abrir a aba AUC (efeito abaixo).
  useEffect(() => {
    fetchAllData();
    fetchDistribuicao(selectedYear);
    // eslint-disable-next-line
  }, [selectedYear]);

  // Primeira entrada na aba AUC: busca AUC, anos de captação e captação (uma vez; o ref guarda).
  useEffect(() => {
    if (activeTab !== 'pl' || plCarregadoRef.current) return;
    plCarregadoRef.current = true;
    fetchAucData();
    fetchCaptacaoAnos();
    fetchCaptacao();
    // eslint-disable-next-line
  }, [activeTab]);
  // Troca de visão/ano da captação (só depois que a aba AUC já foi aberta).
  useEffect(() => {
    if (!plCarregadoRef.current) return;
    fetchCaptacao();
    // eslint-disable-next-line
  }, [captacaoView, captacaoAno]);

  const getAvailableYears = () => {
    const years = new Set([2024, 2025]);
    availableYears.forEach(y => years.add(y));
    // O ano na tela sempre tem opção no select (ex.: ano corrente antes da 1ª resposta).
    if (selectedYear) years.add(selectedYear);
    return Array.from(years).sort((a, b) => b - a);
  };

  // Leads data
  const currentData = selectedType === 'consultoria' ? consultoriaData : 
                      selectedType === 'curso' ? cursoData : 
                      selectedType === 'suica' ? suicaData :
                      selectedType === 'consorcio' ? consorcioData :
                      selectedType === 'anuncio-meta' ? anuncioMetaData :
                      aplicacaoData;
  const allWeeks = currentData?.weekly_metrics || [];
  
  // Filtrar apenas semanas do ano selecionado
  const weeksOfSelectedYear = allWeeks
    .filter(w => w.week && w.week.startsWith(`${selectedYear}-`))
    .sort((a, b) => a.week.localeCompare(b.week));
  
  // Para o gráfico, mostrar últimas 12 semanas COM DADOS (não semanas vazias)
  const weeksWithData = weeksOfSelectedYear.filter(w => w.leads_total > 0);
  const weeksForChart = weeksWithData.slice(-12);
  
  const totalLeads = currentData?.total_leads || 0;
  const weeksWithLeads = weeksWithData.length;
  const avgAnnual = weeksWithLeads > 0 ? (totalLeads / weeksWithLeads).toFixed(1) : 0;
  
  // Para média móvel, pegar últimas 8 semanas com dados
  const last8WeeksWithData = weeksWithData.slice(-8);
  const leadsLast8Weeks = last8WeeksWithData.reduce((sum, w) => sum + w.leads_total, 0);
  const avgMoving8 = last8WeeksWithData.length > 0 ? (leadsLast8Weeks / last8WeeksWithData.length).toFixed(1) : '0.0';

  // ── Gráfico da APLICAÇÃO: as barras devem refletir os filtros ativos
  // (Status/Origem/Consultor/Reunião/Busca). Recalculamos no cliente a partir
  // dos MESMOS leads filtrados da lista, bucketizando por semana/mês ISO em BRT
  // (idêntico ao backend week_utils). Os cards de cima (Total/Média) seguem com
  // os totais do backend. Outros tipos seguem com weekly/monthly_metrics.
  const isAplicacao = selectedType === 'aplicacao';

  // Leads da Aplicação com os filtros da tela (Status/Origem/Consultor/Reunião/Busca).
  // Memo: só recalcula quando dados ou filtros mudam (não a cada render, ex.: expandir
  // um lead). A lista abaixo reaproveita este mesmo resultado. A busca usa o valor
  // adiado: o campo responde na hora e o filtro roda logo depois.
  const aplicacaoLeadsFiltrados = useMemo(() => {
    let leads = aplicacaoData?.leads || [];
    // Médicos não têm qualificação automática (qualificado=null). Eles sempre
    // aparecem na lista junto com o resto — "furam" o filtro de status
    // (Qualif/Desq) pra não sumirem na visão padrão. O badge 'Médico' distingue.
    if (origemFilter !== 'medicos') {
      if (aplicacaoFilter === 'qualificados') {
        leads = leads.filter(l => l.qualificado === true || l.origem === 'Médicos');
      } else if (aplicacaoFilter === 'desqualificados') {
        leads = leads.filter(l => l.qualificado === false || l.origem === 'Médicos');
      }
    }
    // Filtro por origem. Orgânico inclui Médicos (origem fica clara no badge); só exclui Anúncio.
    if (origemFilter === 'organico') {
      leads = leads.filter(l => l.origem !== 'Anúncio');
    } else if (origemFilter === 'anuncio') {
      leads = leads.filter(l => l.origem === 'Anúncio');
    } else if (origemFilter === 'medicos') {
      leads = leads.filter(l => l.origem === 'Médicos');
    }
    // Filtro por consultor (tag CL/MH)
    if (consultorTagFilter === 'CL') {
      leads = leads.filter(l => (l.consultor_tag || 'CL') === 'CL');
    } else if (consultorTagFilter === 'MH') {
      leads = leads.filter(l => l.consultor_tag === 'MH');
    }
    // Filtro por reunião (sem reunião agendada nem realizada)
    if (reuniaoFilter === 'sem_reuniao') {
      leads = leads.filter(l => !l.reuniao_agendada && !l.reuniao_realizada);
    }
    // Filtro por nome
    if (searchDeferido) {
      const termo = searchDeferido.toLowerCase();
      leads = leads.filter(l => {
        const nome = (l.nome || `${l.respostas?.oM9hIYlRwMQ9 || ''} ${l.respostas?.DPIy4hvUaGat || ''}`).toLowerCase();
        return nome.includes(termo);
      });
    }
    return leads;
  }, [aplicacaoData, origemFilter, aplicacaoFilter, consultorTagFilter, reuniaoFilter, searchDeferido]);

  // Agrupamentos semanal e mensal da Aplicação (memo: só quando os leads filtrados ou o ano mudam).
  const semanasAplicacao = useMemo(() => (
    bucketLeads(aplicacaoLeadsFiltrados, isoWeekKeyBRT, 'week')
      .filter(w => w.week.startsWith(`${selectedYear}-`))
      .sort((a, b) => a.week.localeCompare(b.week))
      .filter(w => w.leads_total > 0)
      .slice(-12)
  ), [aplicacaoLeadsFiltrados, selectedYear]);
  const mesesAplicacao = useMemo(() => (
    bucketLeads(aplicacaoLeadsFiltrados, monthKeyBRT, 'month')
      .sort((a, b) => (a.month || '').localeCompare(b.month || ''))
  ), [aplicacaoLeadsFiltrados]);

  const weeksForChartEnriched = (isAplicacao ? semanasAplicacao : weeksForChart)
    .map(w => ({ ...w, reunioes_organico: w.reunioes_organico || 0, reunioes_anuncio: w.reunioes_anuncio || 0 }));

  // Get monthly data
  const monthsForChart = (isAplicacao
    ? mesesAplicacao
    : (currentData?.monthly_metrics || [])
  ).slice(-12);
  const monthsForChartEnriched = monthsForChart.map(m => ({
    ...m,
    reunioes_organico: m.reunioes_organico || 0,
    reunioes_anuncio: m.reunioes_anuncio || 0,
  }));

  const formatWeekLabel = (week) => {
    if (!week) return '';
    const parts = week.split('-W');
    return `S${parts[1]}`;
  };

  // Período (DD/MM a DD/MM) da semana, usando a MESMA convenção do backend:
  // semana ISO 8601 — segunda → domingo (a semana 1 pode puxar fim de dezembro).
  // A semana 1 contém a primeira quinta-feira do ano; segunda = jan/4 menos seu
  // dia-da-semana (com segunda=0) mais (wk-1) semanas.
  const formatWeekRange = (week) => {
    if (!week) return '';
    const parts = week.split('-W');
    const year = parseInt(parts[0], 10);
    const wk = parseInt(parts[1], 10);
    if (!year || !wk) return '';
    const jan4 = new Date(year, 0, 4);
    const jan4Day = (jan4.getDay() + 6) % 7; // 0 = segunda
    const start = new Date(jan4);
    start.setDate(jan4.getDate() - jan4Day + (wk - 1) * 7); // segunda da semana ISO
    const end = new Date(start);
    end.setDate(start.getDate() + 6); // domingo
    const fmt = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
    return `${fmt(start)} a ${fmt(end)}`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatMonth = (refMonth) => {
    if (!refMonth) return '';
    const [year, month] = refMonth.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${months[parseInt(month) - 1]}/${year.slice(2)}`;
  };

  const formatMonthShort = (refMonth) => {
    if (!refMonth) return '';
    const [year, month] = refMonth.split('-');
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return months[parseInt(month) - 1];
  };

  // Consultoria opera por response_id (mesmo padrão da Aplicação),
  // contra a tabela leads_consultoria persistida em PG.
  // Atualiza um lead da Consultoria só na tela (antes cada ação recarregava o painel
  // inteiro: 6 rotas, Typeform e planilha). `recent_leads` traz os mesmos leads.
  const atualizarConsultoriaLocal = (responseId, campos) => {
    const aplicar = (lista) => (Array.isArray(lista)
      ? lista.map((l) => (l.response_id === responseId ? { ...l, ...campos } : l))
      : lista);
    setConsultoriaData((prev) => (prev ? { ...prev, leads: aplicar(prev.leads), recent_leads: aplicar(prev.recent_leads) } : prev));
  };
  const leadConsultoria = (responseId) => (consultoriaData?.leads || []).find((l) => l.response_id === responseId);

  // Troca de consultor: a tela muda na hora; se o servidor recusar, volta ao anterior.
  const salvarConsultorConsultoria = async (responseId, consultor) => {
    if (!responseId) return;
    const anterior = leadConsultoria(responseId)?.consultor_responsavel;
    const desfazer = () => atualizarConsultoriaLocal(responseId, { consultor_responsavel: anterior });
    atualizarConsultoriaLocal(responseId, { consultor_responsavel: consultor || '' });
    try {
      const res = await authedFetch(
        `${backendUrl}/api/typeform/leads/consultoria/${encodeURIComponent(responseId)}/consultor-by-id?consultor=${encodeURIComponent(consultor || '')}`,
        { method: 'PUT' }
      );
      if (!res.ok) desfazer();
    } catch (err) {
      console.error('Erro:', err);
      desfazer();
    }
  };

  // Desmarcar "Contato" (reseta o envio de e-mail): a tela muda na hora; erro desfaz.
  const resetarContatoConsultoria = async (responseId) => {
    const lead = leadConsultoria(responseId);
    const desfazer = () => atualizarConsultoriaLocal(responseId, {
      email_consultor_enviado: lead?.email_consultor_enviado,
      email_consultor_enviado_em: lead?.email_consultor_enviado_em,
    });
    atualizarConsultoriaLocal(responseId, { email_consultor_enviado: false, email_consultor_enviado_em: null });
    try {
      const res = await authedFetch(
        `${backendUrl}/api/typeform/leads/consultoria/${encodeURIComponent(responseId)}/reset-email-by-id`,
        { method: 'PUT' }
      );
      if (!res.ok) desfazer();
    } catch (err) {
      console.error('Erro:', err);
      desfazer();
    }
  };

  const enviarEmailConsultoria = async (responseId, consultor) => {
    if (!consultor) {
      setEmailMessage({ type: 'error', text: 'Selecione um consultor primeiro' });
      setTimeout(() => setEmailMessage(null), 3000);
      return;
    }
    setSendingEmail(prev => ({ ...prev, [responseId]: true }));
    setEmailMessage(null);
    try {
      const res = await authedFetch(
        `${backendUrl}/api/typeform/leads/consultoria/${encodeURIComponent(responseId)}/enviar-email?consultor=${encodeURIComponent(consultor)}`,
        { method: 'POST' }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setEmailMessage({ type: 'success', text: `E-mail enviado para ${consultor}!` });
        // Mesmos campos que o servidor grava ao enviar.
        atualizarConsultoriaLocal(responseId, {
          consultor_responsavel: consultor,
          email_consultor_enviado: true,
          email_consultor_enviado_em: new Date().toISOString(),
        });
      } else {
        setEmailMessage({ type: 'error', text: data.detail || 'Erro ao enviar e-mail' });
      }
    } catch (err) {
      console.error('Erro ao enviar e-mail:', err);
      setEmailMessage({ type: 'error', text: 'Erro de conexão ao enviar e-mail' });
    } finally {
      setSendingEmail(prev => ({ ...prev, [responseId]: false }));
      setTimeout(() => setEmailMessage(null), 4000);
    }
  };

  // AUC chart data
  const aucHistory = aucData?.history || [];

  // Série mensal do AUC (fim de cada mês), derivada da série diária real.
  // Centralizado num único memo — antes era recalculado inline em 3 lugares
  // (Variação de PL, gráfico de evolução e histórico), com risco de divergir.
  const aucMonthly = React.useMemo(() => {
    const hist = aucData?.btg_daily_history;
    if (!hist || hist.length === 0) return null;
    const mk = (d) => (d.ref_date || '').slice(0, 7);
    const btgAsc = [...hist].reverse();
    const xpAsc = [...(aucData.xp_daily_history || [])].reverse();
    const monthEnd = (arr) => { const o = {}; arr.forEach((d) => { if (d.pl_total != null) o[mk(d)] = d; }); return o; };
    const btgME = monthEnd(btgAsc);
    const xpME = monthEnd(xpAsc.filter((d) => d.pl_total > 0));
    const months = Array.from(new Set([...Object.keys(btgME), ...Object.keys(xpME)])).sort();
    return { btgAsc, xpAsc, btgME, xpME, months };
  }, [aucData]);

  // Formatação pt-BR consistente com o resto do painel (vírgula decimal).
  const MES_AUC = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
  const mLabelAuc = (k) => { const [y, m] = k.split('-'); return `${MES_AUC[parseInt(m, 10) - 1]}/${y.slice(2)}`; };
  const fmtMilhoes = (v) => `${(v / 1e6).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;

  // View mode for leads chart
  const [leadsViewMode, setLeadsViewMode] = React.useState('weekly'); // 'weekly' or 'monthly'

  // Seções do painel na barra lateral (a do CareOS/hub), na mesma ordem das
  // antigas abas do topo. Administração só para admin.
  const gruposPainel = [
    { titulo: 'Visão geral', itens: [
      { id: 'projetos', rotulo: 'Projetos', Icone: SquaresFour },
      { id: 'leads', rotulo: 'Leads', Icone: Users },
      { id: 'pl', rotulo: 'AUC', Icone: Wallet },
      { id: 'receitas', rotulo: 'Receitas', Icone: CurrencyDollar },
    ] },
    { titulo: 'Comercial', itens: [
      { id: 'pipeline', rotulo: 'Pipeline', Icone: TrendUp },
      { id: 'reunioes', rotulo: 'Reuniões', Icone: VideoCamera },
      { id: 'pos-reuniao', rotulo: 'Pós-Reunião', Icone: ClipboardText },
    ] },
    ...(ehAdminPainel(user) ? [{ titulo: 'Administração', itens: [
      { id: 'visao-consolidada', rotulo: 'Visão consolidada', Icone: ChartPieSlice },
      { id: 'admin', rotulo: 'Admin', Icone: GearSix },
    ] }] : []),
  ];
  const secaoAtiva = gruposPainel.flatMap((g) => g.itens).find((i) => i.id === activeTab);
  const tiposLead = [
    { id: 'consultoria', rotulo: 'Consultoria' },
    { id: 'curso', rotulo: 'Curso' },
    { id: 'aplicacao', rotulo: 'Aplicação' },
    { id: 'suica', rotulo: 'Suíça' },
    { id: 'consorcio', rotulo: 'Consórcio' },
  ];

  // Variação com sinal explícito (DS v3 §2.5): + / − (U+2212) / nada no zero; zero em neutral-text.
  const sinal = (v) => (v > 0 ? '+' : v < 0 ? '−' : '');
  const corVariacao = (v) => (v > 0
    ? 'text-[color:var(--care-positive-text)]'
    : v < 0 ? 'text-[color:var(--care-negative-text)]' : 'text-[color:var(--care-neutral-text)]');
  const fmtVar = (v) => `${sinal(v)}${formatCurrency(Math.abs(v))}`;

  return (
    <MotionConfig reducedMotion="user">
    <div className="app-bloco">
      <BarraLateral
        grupos={gruposPainel}
        ativo={activeTab}
        onSelecionar={setActiveTab}
        user={user}
        onSair={() => { logout(); navigate('/login'); }}
      />
      <div className="ui-pagina">

        {/* Header */}
        <header className="ui-tela">
          <div className="ui-tela__texto">
            <span className="ui-eyebrow" data-tom="acento">Gerencial</span>
            <h1 className="ui-tela__titulo">{secaoAtiva?.rotulo || 'Dashboard'}</h1>
          </div>
          <div className="ui-tela__acoes">
            <button
              type="button"
              className="ui-botao"
              data-variante="fantasma"
              data-so-icone
              onClick={() => {
                // Força a rodada completa do ano na tela (e a distribuição). O AUC só
                // é refeito se a aba AUC já foi aberta; senão carrega ao abri-la.
                fetchAllData();
                fetchDistribuicao(selectedYear);
                if (plCarregadoRef.current) fetchAucData();
              }}
              disabled={loading}
              aria-busy={loading || undefined}
              aria-label="Atualizar dados"
              title="Atualizar dados"
            >
              <ArrowClockwise size={16} weight="bold" aria-hidden="true" className={loading ? 'motion-safe:animate-spin' : undefined} />
            </button>
          </div>
        </header>

        <main id="painel-principal" aria-label={secaoAtiva?.rotulo}>

        {/* LEADS TAB */}
        {/* Carga inicial: esqueleto só no lugar do conteúdo (cabeçalho e abas ficam).
            Erro no fetch principal sem nenhum dado carregado — mesmo padrão do aucError. */}
        {activeTab === 'leads' && (loading && !consultoriaData && !cursoData ? (
          <TabSkeleton label="Carregando..." />
        ) : leadsError && !consultoriaData && !cursoData && !aplicacaoData ? (
          <div className="ui-card">
            <div className="ui-vazio-bloco" data-area role="alert">
              <p className="ui-vazio">Não foi possível carregar os leads</p>
              <button type="button" className="ui-vazio__caminho" onClick={fetchAllData}>
                Tentar de novo <ArrowRight size={16} weight="bold" aria-hidden="true" />
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Tipo de lead (sub-abas) + ano, em fileiras próprias */}
            <div className="flex flex-wrap items-end gap-x-6 gap-y-2 mb-6">
              <div className="ui-abas mb-0 flex-1 min-w-0" role="tablist" aria-label="Tipo de lead" onKeyDown={moverFocoAba}>
                {tiposLead.map(({ id, rotulo }) => {
                  const sel = selectedType === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      className="ui-aba"
                      data-tamanho="sm"
                      role="tab"
                      id={`tipo-${id}`}
                      aria-selected={sel}
                      aria-controls="painel-tipo-lead"
                      tabIndex={tabIndexAba(tiposLead, selectedType, id)}
                      data-ativa={sel || undefined}
                      onClick={() => setSelectedType(id)}
                    >
                      {rotulo}
                    </button>
                  );
                })}
              </div>
              <div className="ui-rotulo inline-flex items-center gap-2 pb-1 shrink-0">
                <CalendarBlank size={16} weight="bold" aria-hidden="true" />
                <select
                  className="ui-campo"
                  data-tamanho="sm"
                  aria-label="Ano"
                  value={selectedYear || ''}
                  onChange={(e) => { anoDecididoRef.current = true; setSelectedYear(parseInt(e.target.value)); }}
                >
                  {getAvailableYears().map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            <div role="tabpanel" id="painel-tipo-lead" aria-labelledby={`tipo-${selectedType}`}>

            {leadsFalhas.includes(selectedType) && (
              <p className="ui-aviso mb-4" data-tom="negativo" role="alert">
                <WarningCircle size={16} weight="bold" aria-hidden="true" />
                Não foi possível carregar os leads de {NOME_TIPO_LEAD[selectedType] || selectedType}; os números abaixo podem estar incompletos ou desatualizados.
              </p>
            )}

            {/* Stats Cards */}
            <div className={`grid ${selectedType === 'aplicacao' ? 'grid-cols-2 lg:grid-cols-5' : 'grid-cols-2 lg:grid-cols-4'} gap-3 sm:gap-4 mb-6 sm:mb-8`}>
              <div className="ui-card" data-densidade="compacta">
                <div className="ui-kpi" data-tamanho="lg">
                  <span className="ui-rotulo inline-flex items-center gap-2"><Users size={16} weight="bold" aria-hidden="true" />Total no Ano</span>
                  <span className="ui-kpi__valor"><AnimNum value={totalLeads} /></span>
                </div>
              </div>

              <div className="ui-card" data-densidade="compacta">
                <div className="ui-kpi" data-tamanho="lg">
                  <span className="ui-rotulo inline-flex items-center gap-2"><CalendarBlank size={16} weight="bold" aria-hidden="true" />Semanas c/ Leads</span>
                  <span className="ui-kpi__valor"><AnimNum value={weeksWithLeads} /></span>
                </div>
              </div>

              <div className="ui-card" data-densidade="compacta">
                <div className="ui-kpi" data-tamanho="lg">
                  <span className="ui-rotulo inline-flex items-center gap-2"><ChartBar size={16} weight="bold" aria-hidden="true" />Média Anual</span>
                  <span className="ui-kpi__valor"><AnimDecimal value={parseFloat(avgAnnual) || 0} suffix="" /></span>
                  <span className="ui-kpi__rotulo">por semana</span>
                </div>
              </div>

              {selectedType === 'aplicacao' ? (
                <>
                  <div className="ui-card" data-densidade="compacta">
                    <div className="ui-kpi" data-tamanho="lg">
                      <span className="ui-rotulo inline-flex items-center gap-2"><Pulse size={16} weight="bold" aria-hidden="true" />Qualificados</span>
                      <span className="ui-kpi__valor"><AnimNum value={aplicacaoData?.qualificados || 0} /></span>
                      <span className="ui-kpi__rotulo">
                        {aplicacaoData?.qualificados && totalLeads > 0
                          ? ((aplicacaoData.qualificados / totalLeads) * 100).toFixed(1).replace('.', ',')
                          : 0}% do total
                      </span>
                    </div>
                  </div>
                  <div className="ui-card" data-densidade="compacta">
                    <span className="ui-rotulo inline-flex items-center gap-2"><UserCheck size={16} weight="bold" aria-hidden="true" />Reuniões</span>
                    <div className="ui-trilho" data-gap="6" data-alinhar="base">
                      <div className="ui-kpi" data-tamanho="lg">
                        <span className="ui-kpi__valor"><AnimNum value={aplicacaoData?.leads?.filter(l => l.reuniao_agendada).length || 0} /></span>
                        <span className="ui-kpi__rotulo">agendadas</span>
                      </div>
                      <div className="ui-kpi" data-tamanho="lg">
                        <span className="ui-kpi__valor"><AnimNum value={aplicacaoData?.leads?.filter(l => l.reuniao_realizada).length || 0} /></span>
                        <span className="ui-kpi__rotulo">realizadas</span>
                      </div>
                    </div>
                    <div className="ui-legenda">
                      <span className="ui-legenda__item">
                        <span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-cat-2)' }} />
                        Org: <span className="font-medium tabular-nums lining-nums">{aplicacaoData?.leads?.filter(l => l.reuniao_agendada && l.origem !== 'Anúncio').length || 0}</span>
                      </span>
                      <span className="ui-legenda__item">
                        <span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-cat-3)' }} />
                        Anún: <span className="font-medium tabular-nums lining-nums">{aplicacaoData?.leads?.filter(l => l.reuniao_agendada && l.origem === 'Anúncio').length || 0}</span>
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="ui-card" data-densidade="compacta">
                  <div className="ui-kpi" data-tamanho="lg">
                    <span className="ui-rotulo inline-flex items-center gap-2"><Pulse size={16} weight="bold" aria-hidden="true" />Média Móvel</span>
                    <span className="ui-kpi__valor"><AnimDecimal value={parseFloat(avgMoving8) || 0} suffix="" /></span>
                    <span className="ui-kpi__rotulo">últimas 8 sem</span>
                  </div>
                </div>
              )}
            </div>

            {/* Chart */}
            <section className="ui-card mb-8" aria-labelledby="titulo-grafico-leads">
              <div className="ui-cabecalho">
                <h3 id="titulo-grafico-leads" className="ui-titulo" data-tamanho="sm">
                  Leads por {leadsViewMode === 'weekly' ? 'Semana' : 'Mês'}
                </h3>
                <span className="ui-meta">{leadsViewMode === 'weekly' ? 'últimas 12 semanas' : 'últimos 12 meses'}</span>
                <div className="ui-cabecalho__acoes">
                  {/* View Mode Toggle */}
                  <div className="ui-segmentado" role="group" aria-label="Período do gráfico">
                    <button type="button" className="ui-segmentado__opcao" aria-pressed={leadsViewMode === 'weekly'} onClick={() => setLeadsViewMode('weekly')}>
                      Semanal
                    </button>
                    <button type="button" className="ui-segmentado__opcao" aria-pressed={leadsViewMode === 'monthly'} onClick={() => setLeadsViewMode('monthly')}>
                      Mensal
                    </button>
                  </div>
                </div>
              </div>
              
              <AnimatePresence mode="wait">
              {/* Weekly Chart */}
              {leadsViewMode === 'weekly' && weeksForChartEnriched.length > 0 && (
                (() => {
                  const maxValue = Math.max(...weeksForChartEnriched.map(w => w.leads_total));
                  let yMax;
                  if (maxValue <= 5) yMax = 5;
                  else if (maxValue <= 10) yMax = 10;
                  else if (maxValue <= 15) yMax = 15;
                  else if (maxValue <= 20) yMax = 20;
                  else if (maxValue <= 25) yMax = 25;
                  else if (maxValue <= 50) yMax = 50;
                  else yMax = Math.ceil(maxValue / 10) * 10;
                  return (
                    <motion.div key="weekly" className="relative" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2, ease: [0.2, 0, 0, 1] }}>
                      <div className="ui-legenda mb-3">
                        <span className="ui-legenda__item"><span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-cat-1)' }} />Leads</span>
                        <span className="ui-legenda__item"><span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-cat-2)' }} />Reuniões Orgânico</span>
                        <span className="ui-legenda__item"><span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-cat-3)' }} />Reuniões Anúncio</span>
                      </div>
                      <div className="absolute left-0 top-7 bottom-8 w-8 flex flex-col justify-between text-[11px] text-[color:var(--care-chart-label)] tabular-nums lining-nums">
                        <span>{yMax}</span>
                        <span>{yMax / 2}</span>
                        <span>0</span>
                      </div>
                      <div className="ml-10">
                        <div className="absolute left-10 right-0 top-7 h-[200px] flex flex-col justify-between pointer-events-none">
                          <div className="border-t border-[color:var(--care-chart-grid)] w-full"></div>
                          <div className="border-t border-[color:var(--care-chart-grid)] w-full"></div>
                          <div className="border-t border-[color:var(--care-chart-axis)] w-full"></div>
                        </div>
                        <div className="flex items-end gap-2 h-[200px] relative">
                          {weeksForChartEnriched.map((week, idx) => {
                            const leadsH = Math.max((week.leads_total / yMax) * 200, week.leads_total > 0 ? 4 : 2);
                            const orgH = Math.max((week.reunioes_organico / yMax) * 200, week.reunioes_organico > 0 ? 4 : 0);
                            const ancH = Math.max((week.reunioes_anuncio / yMax) * 200, week.reunioes_anuncio > 0 ? 4 : 0);
                            const delay = idx * 0.02; // cascata curta: 12 barras cabem em ~0,5 s (DS v3 §5)
                            return (
                              <div
                                key={idx}
                                className="flex-1 flex items-end justify-center gap-0.5 h-full group relative rounded-sm"
                                tabIndex={0}
                                role="img"
                                aria-label={`Semana ${formatWeekLabel(week.week).replace('S', '')} (${formatWeekRange(week.week)}): ${week.leads_total} leads, ${week.reunioes_organico} reuniões orgânico, ${week.reunioes_anuncio} reuniões anúncio`}
                              >
                                <div aria-hidden="true" className="hidden group-hover:block group-focus-visible:block absolute left-1/2 top-0 -translate-x-1/2 -translate-y-full z-dropdown whitespace-nowrap bg-[var(--care-bg-inverse)] text-[color:var(--care-text-inverse)] text-[11px] leading-tight rounded-md px-2 py-1 [box-shadow:var(--care-shadow-lg)] pointer-events-none">
                                  <div className="font-semibold">Semana {formatWeekLabel(week.week).replace('S', '')} · {formatWeekRange(week.week)}</div>
                                  <div className="text-[color:color-mix(in_srgb,var(--care-text-inverse)_72%,transparent)] mt-0.5 tabular-nums lining-nums">{week.leads_total} leads · {week.reunioes_organico} org · {week.reunioes_anuncio} anúncio</div>
                                </div>
                                <div style={{ width: '32%' }} className="flex flex-col items-center justify-end h-full">
                                  <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: delay + 0.28, duration: 0.2 }} className={CLS_ROTULO_BARRA}>{week.leads_total}</motion.span>
                                  <motion.div className="w-full rounded-t-sm hover:opacity-80" style={{ height: `${leadsH}px`, backgroundColor: week.leads_total > 0 ? 'var(--care-chart-cat-1)' : 'var(--care-chart-grid)', transformOrigin: 'bottom' }} initial={{ scaleY: 0, opacity: 0 }} animate={{ scaleY: 1, opacity: 1 }} transition={{ delay, duration: 0.28, ease: [0.2, 0, 0, 1] }} title={`${formatWeekLabel(week.week)} (${formatWeekRange(week.week)}) — ${week.leads_total} leads`} />
                                </div>
                                <div style={{ width: '32%' }} className="flex flex-col items-center justify-end h-full">
                                  {week.reunioes_organico > 0 && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: delay + 0.28, duration: 0.2 }} className={CLS_ROTULO_BARRA}>{week.reunioes_organico}</motion.span>}
                                  <motion.div className="w-full rounded-t-sm hover:opacity-80" style={{ height: `${orgH}px`, backgroundColor: week.reunioes_organico > 0 ? 'var(--care-chart-cat-2)' : 'var(--care-chart-grid)', transformOrigin: 'bottom' }} initial={{ scaleY: 0, opacity: 0 }} animate={{ scaleY: 1, opacity: 1 }} transition={{ delay: delay + 0.08, duration: 0.28, ease: [0.2, 0, 0, 1] }} title={`${formatWeekLabel(week.week)} (${formatWeekRange(week.week)}) — ${week.reunioes_organico} reuniões orgânico`} />
                                </div>
                                <div style={{ width: '32%' }} className="flex flex-col items-center justify-end h-full">
                                  {week.reunioes_anuncio > 0 && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: delay + 0.28, duration: 0.2 }} className={CLS_ROTULO_BARRA}>{week.reunioes_anuncio}</motion.span>}
                                  <motion.div className="w-full rounded-t-sm hover:opacity-80" style={{ height: `${ancH}px`, backgroundColor: week.reunioes_anuncio > 0 ? 'var(--care-chart-cat-3)' : 'var(--care-chart-grid)', transformOrigin: 'bottom' }} initial={{ scaleY: 0, opacity: 0 }} animate={{ scaleY: 1, opacity: 1 }} transition={{ delay: delay + 0.16, duration: 0.28, ease: [0.2, 0, 0, 1] }} title={`${formatWeekLabel(week.week)} (${formatWeekRange(week.week)}) — ${week.reunioes_anuncio} reuniões anúncio`} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex gap-2 mt-3">
                          {weeksForChartEnriched.map((week, idx) => (
                            <div key={idx} className="flex-1 text-center text-[11px] text-[color:var(--care-chart-label)]">
                              {formatWeekLabel(week.week)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  );
                })()
              )}

              {/* Monthly Chart */}
              {leadsViewMode === 'monthly' && monthsForChartEnriched.length > 0 && (
                (() => {
                  const maxValue = Math.max(...monthsForChartEnriched.map(m => m.leads_total));
                  let yMax;
                  if (maxValue <= 5) yMax = 5;
                  else if (maxValue <= 10) yMax = 10;
                  else if (maxValue <= 15) yMax = 15;
                  else if (maxValue <= 20) yMax = 20;
                  else if (maxValue <= 25) yMax = 25;
                  else if (maxValue <= 50) yMax = 50;
                  else yMax = Math.ceil(maxValue / 10) * 10;
                  return (
                    <motion.div key="monthly" className="relative" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2, ease: [0.2, 0, 0, 1] }}>
                      <div className="ui-legenda mb-3">
                        <span className="ui-legenda__item"><span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-cat-1)' }} />Leads</span>
                        <span className="ui-legenda__item"><span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-cat-2)' }} />Reuniões Orgânico</span>
                        <span className="ui-legenda__item"><span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-cat-3)' }} />Reuniões Anúncio</span>
                      </div>
                      <div className="absolute left-0 top-7 bottom-8 w-8 flex flex-col justify-between text-[11px] text-[color:var(--care-chart-label)] tabular-nums lining-nums">
                        <span>{yMax}</span>
                        <span>{yMax / 2}</span>
                        <span>0</span>
                      </div>
                      <div className="ml-10">
                        <div className="absolute left-10 right-0 top-7 h-[200px] flex flex-col justify-between pointer-events-none">
                          <div className="border-t border-[color:var(--care-chart-grid)] w-full"></div>
                          <div className="border-t border-[color:var(--care-chart-grid)] w-full"></div>
                          <div className="border-t border-[color:var(--care-chart-axis)] w-full"></div>
                        </div>
                        <div className="flex items-end gap-4 h-[200px] relative">
                          {monthsForChartEnriched.map((month, idx) => {
                            const leadsH = Math.max((month.leads_total / yMax) * 200, month.leads_total > 0 ? 4 : 2);
                            const orgH = Math.max((month.reunioes_organico / yMax) * 200, month.reunioes_organico > 0 ? 4 : 0);
                            const ancH = Math.max((month.reunioes_anuncio / yMax) * 200, month.reunioes_anuncio > 0 ? 4 : 0);
                            const delay = idx * 0.02; // cascata curta: 12 barras cabem em ~0,5 s (DS v3 §5)
                            return (
                              <div
                                key={idx}
                                className="flex-1 flex items-end justify-center gap-0.5 h-full min-w-[40px]"
                                role="img"
                                aria-label={`${formatMonthShort(month.month)}: ${month.leads_total} leads, ${month.reunioes_organico} reuniões orgânico, ${month.reunioes_anuncio} reuniões anúncio`}
                              >
                                <div style={{ width: '32%' }} className="flex flex-col items-center justify-end h-full">
                                  <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: delay + 0.28, duration: 0.2 }} className={CLS_ROTULO_BARRA}>{month.leads_total}</motion.span>
                                  <motion.div className="w-full rounded-t-sm hover:opacity-80" style={{ height: `${leadsH}px`, backgroundColor: month.leads_total > 0 ? 'var(--care-chart-cat-1)' : 'var(--care-chart-grid)', transformOrigin: 'bottom' }} initial={{ scaleY: 0, opacity: 0 }} animate={{ scaleY: 1, opacity: 1 }} transition={{ delay, duration: 0.28, ease: [0.2, 0, 0, 1] }} title={`${month.leads_total} leads`} />
                                </div>
                                <div style={{ width: '32%' }} className="flex flex-col items-center justify-end h-full">
                                  {month.reunioes_organico > 0 && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: delay + 0.28, duration: 0.2 }} className={CLS_ROTULO_BARRA}>{month.reunioes_organico}</motion.span>}
                                  <motion.div className="w-full rounded-t-sm hover:opacity-80" style={{ height: `${orgH}px`, backgroundColor: month.reunioes_organico > 0 ? 'var(--care-chart-cat-2)' : 'var(--care-chart-grid)', transformOrigin: 'bottom' }} initial={{ scaleY: 0, opacity: 0 }} animate={{ scaleY: 1, opacity: 1 }} transition={{ delay: delay + 0.08, duration: 0.28, ease: [0.2, 0, 0, 1] }} title={`${month.reunioes_organico} reuniões orgânico`} />
                                </div>
                                <div style={{ width: '32%' }} className="flex flex-col items-center justify-end h-full">
                                  {month.reunioes_anuncio > 0 && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: delay + 0.28, duration: 0.2 }} className={CLS_ROTULO_BARRA}>{month.reunioes_anuncio}</motion.span>}
                                  <motion.div className="w-full rounded-t-sm hover:opacity-80" style={{ height: `${ancH}px`, backgroundColor: month.reunioes_anuncio > 0 ? 'var(--care-chart-cat-3)' : 'var(--care-chart-grid)', transformOrigin: 'bottom' }} initial={{ scaleY: 0, opacity: 0 }} animate={{ scaleY: 1, opacity: 1 }} transition={{ delay: delay + 0.16, duration: 0.28, ease: [0.2, 0, 0, 1] }} title={`${month.reunioes_anuncio} reuniões anúncio`} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex gap-4 mt-3">
                          {monthsForChartEnriched.map((month, idx) => (
                            <div key={idx} className="flex-1 text-center text-[11px] text-[color:var(--care-chart-label)] min-w-[40px]">
                              {formatMonthShort(month.month)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  );
                })()
              )}
              </AnimatePresence>

              {((leadsViewMode === 'weekly' && weeksForChartEnriched.length === 0) ||
                (leadsViewMode === 'monthly' && monthsForChartEnriched.length === 0)) && (
                <div className="h-[200px] flex items-center justify-center">
                  <p className="ui-vazio">Nenhum dado disponível</p>
                </div>
              )}
            </section>

            {/* Histogramas de Distribuição - Só aparece quando Aplicação está selecionada */}
            {selectedType === 'aplicacao' && distribuicaoData && (
              <div className="ui-card overflow-hidden" style={{ padding: 0, gap: 0 }} data-testid="distribution-histograms">
                <div className="ui-cabecalho px-5 py-4 border-b border-[color:var(--care-border-subtle)]">
                  <h3 className="ui-titulo" data-tamanho="sm">Distribuição das Respostas</h3>
                  <span className="ui-meta tabular-nums lining-nums">{selectedYear}</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-[color:var(--care-border-subtle)]">
                  {/* Histogramas de Renda, Patrimônio e Capacidade de Aporte.
                      A contagem sai da barra (fica à direita da trilha, em cor de rótulo):
                      dentro da barra neutral-700 o texto escuro não passava de 2:1. */}
                  {['renda', 'patrimonio', 'aporte'].map((campo) => {
                    const hist = distribuicaoData[campo];
                    return (
                      <div key={campo} className="p-5" data-testid={`histogram-${campo}`}>
                        <h4 className="ui-rotulo mb-4">{hist?.titulo}</h4>
                        <div className="space-y-2">
                          {hist?.dados?.map((item, idx) => {
                            const maxVal = Math.max(...hist.dados.map(d => d.quantidade), 1);
                            const percentage = (item.quantidade / maxVal) * 100;
                            return (
                              <div key={idx} className="flex items-center gap-2">
                                <span className="text-[11px] text-[color:var(--care-text-secondary)] w-14 text-right tabular-nums lining-nums">{item.faixa}</span>
                                <div className="flex-1 h-5 bg-[var(--care-bg-secondary)] rounded-sm overflow-hidden">
                                  <div
                                    className="h-full rounded-sm transition-[width] duration-slow ease-care"
                                    style={{
                                      width: `${Math.max(percentage, item.quantidade > 0 ? 10 : 0)}%`,
                                      backgroundColor: DISTRIBUTION_BAR_COLORS[campo],
                                    }}
                                  />
                                </div>
                                <span className="w-8 text-right text-[11px] text-[color:var(--care-chart-label)] tabular-nums lining-nums">{item.quantidade}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tabela de Leads - Consultoria */}
            {selectedType === 'consultoria' && consultoriaData && (
              <div className="ui-card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
                  <div className="ui-segmentado" role="group" aria-label="Filtrar leads">
                    <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={consultoriaFilter === 'aprovados'} onClick={() => setConsultoriaFilter('aprovados')}>
                      <span aria-hidden="true">✓</span> Aprovados (<span className="tabular-nums lining-nums">{consultoriaData?.leads_aprovados || 0}</span>)
                    </button>
                    <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={consultoriaFilter === 'nao_aprovados'} onClick={() => setConsultoriaFilter('nao_aprovados')}>
                      <span aria-hidden="true">✗</span> Não Aprov. (<span className="tabular-nums lining-nums">{consultoriaData?.leads_nao_aprovados || 0}</span>)
                    </button>
                    <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={consultoriaFilter === 'todos'} onClick={() => setConsultoriaFilter('todos')}>
                      Todos (<span className="tabular-nums lining-nums">{consultoriaData?.total_leads || 0}</span>)
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-[color:var(--border)] max-h-[600px] overflow-y-auto">
                  {(() => {
                    let leadsFiltrados = consultoriaData?.leads || [];

                    if (consultoriaFilter === 'aprovados') {
                      leadsFiltrados = leadsFiltrados.filter(l => l.status === 'aprovado');
                    } else if (consultoriaFilter === 'nao_aprovados') {
                      leadsFiltrados = leadsFiltrados.filter(l => l.status === 'nao_aprovado');
                    }

                    if (leadsFiltrados.length === 0) {
                      return (
                        <p className="ui-vazio text-center py-8">
                          Nenhum lead encontrado
                        </p>
                      );
                    }

                    return leadsFiltrados.map((lead) => {
                      const chave = `consultoria-${lead.response_id}`;
                      const isExpanded = expandedLeads.has(chave);
                      const alternar = () => {
                        const newExpanded = new Set(expandedLeads);
                        if (isExpanded) {
                          newExpanded.delete(chave);
                        } else {
                          newExpanded.add(chave);
                        }
                        setExpandedLeads(newExpanded);
                      };
                      const aprovado = lead.status === 'aprovado';
                      const enviado = !!lead.email_consultor_enviado;
                      const enviando = !!sendingEmail[lead.response_id];
                      const resetarContato = (e) => { if (!e.target.checked) resetarContatoConsultoria(lead.response_id); };
                      const tituloContato = enviado ? 'Desmarcar contato (reseta o envio de e-mail)' : 'Marcado automaticamente ao enviar o e-mail para o consultor';
                      return (
                        <div key={lead.response_id}>
                          <div className="ui-linha py-3 gap-4" data-interativa onClick={alternar}>
                            {/* Mobile layout */}
                            <div className="flex-1 min-w-0 sm:hidden">
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] tabular-nums lining-nums">{formatDateTime(lead.submitted_at)}</p>
                                  <p className="text-xs text-[color:var(--care-text-secondary)]">{lead.renda || '-'} · {lead.patrimonio || '-'}</p>
                                </div>
                                <span className="ui-pilula" data-tom={aprovado ? 'positivo' : 'negativo'}>
                                  <span aria-hidden="true">{aprovado ? '✓' : '✗'}</span>
                                  <span className="sr-only">{aprovado ? 'Aprovado' : 'Não aprovado'}</span>
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-[color:var(--care-border-subtle)]" onClick={e => e.stopPropagation()}>
                                <select
                                  value={lead.consultor_responsavel || ''}
                                  onChange={(e) => salvarConsultorConsultoria(lead.response_id, e.target.value)}
                                  className="ui-campo flex-1 min-w-0"
                                  data-tamanho="sm"
                                  aria-label="Consultor responsável"
                                >
                                  {renderConsultorOptions('Consultor')}
                                </select>
                                <button
                                  type="button"
                                  className="ui-botao"
                                  data-tamanho="sm"
                                  data-so-icone
                                  data-variante={enviado ? 'fio' : 'solido'}
                                  onClick={() => enviarEmailConsultoria(lead.response_id, lead.consultor_responsavel)}
                                  aria-label={enviado ? 'Reenviar e-mail ao consultor (já enviado)' : 'Enviar e-mail ao consultor'}
                                  aria-busy={enviando || undefined}
                                  disabled={!lead.consultor_responsavel || sendingEmail[lead.response_id]}
                                >
                                  <IconeEnvio enviando={enviando} enviado={enviado} />
                                </button>
                                <label className={CLS_LABEL_CHECK} onClick={e => e.stopPropagation()}>
                                  {/* Marcar só acontece via envio de e-mail (botão ao lado); o checkbox serve pra desfazer */}
                                  <input type="checkbox" checked={lead.email_consultor_enviado || false} disabled={!lead.email_consultor_enviado} title={tituloContato} onChange={resetarContato} className={CLS_CHECKBOX} />
                                  Contato
                                </label>
                              </div>
                            </div>
                            {/* Desktop layout */}
                            <div className="flex-1 hidden sm:grid grid-cols-7 gap-3">
                              <div>
                                <p className="ui-rotulo">Data</p>
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] tabular-nums lining-nums">{formatDateTime(lead.submitted_at)}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Renda</p>
                                <p className="text-[15px] text-[color:var(--care-text-secondary)]">{lead.renda || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Patrimônio</p>
                                <p className="text-[15px] text-[color:var(--care-text-secondary)]">{lead.patrimonio || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Aporte</p>
                                <p className="text-[15px] text-[color:var(--care-text-secondary)]">{lead.aporte || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Status</p>
                                <span className="ui-pilula" data-tom={aprovado ? 'positivo' : 'negativo'}>
                                  <span aria-hidden="true">{aprovado ? '✓' : '✗'}</span> {aprovado ? 'Aprovado' : 'Não Aprov.'}
                                </span>
                              </div>
                              <div onClick={(e) => e.stopPropagation()}>
                                <p className="ui-rotulo">Consultor</p>
                                <div className="flex items-center gap-1">
                                  <select
                                    value={lead.consultor_responsavel || ''}
                                    onChange={(e) => salvarConsultorConsultoria(lead.response_id, e.target.value)}
                                    className="ui-campo min-w-0"
                                    data-tamanho="sm"
                                    aria-label="Consultor responsável"
                                  >
                                    {renderConsultorOptions('Selecionar')}
                                  </select>
                                  <button
                                    type="button"
                                    className="ui-botao"
                                    data-tamanho="sm"
                                    data-so-icone
                                    data-variante={enviado ? 'fio' : 'solido'}
                                    onClick={() => enviarEmailConsultoria(lead.response_id, lead.consultor_responsavel)}
                                    aria-label={enviado ? 'Reenviar e-mail ao consultor (já enviado)' : 'Enviar e-mail ao consultor'}
                                    aria-busy={enviando || undefined}
                                    disabled={!lead.consultor_responsavel || sendingEmail[lead.response_id]}
                                    title={enviado ? 'E-mail já enviado (clique para reenviar)' : 'Enviar e-mail'}
                                  >
                                    <IconeEnvio enviando={enviando} enviado={enviado} />
                                  </button>
                                </div>
                              </div>
                              <label className={`${CLS_LABEL_CHECK} self-center`} onClick={(e) => e.stopPropagation()}>
                                {/* Marcar só acontece via envio de e-mail (botão ao lado); o checkbox serve pra desfazer */}
                                <input type="checkbox" checked={lead.email_consultor_enviado || false} disabled={!lead.email_consultor_enviado} title={tituloContato} onChange={resetarContato} className={CLS_CHECKBOX} />
                                Contato
                              </label>
                            </div>
                            <BotaoExpandir aberto={isExpanded} controla={`lead-${chave}`} onAlternar={alternar} />
                          </div>

                          {isExpanded && (
                            <div id={`lead-${chave}`} className="p-6 bg-[var(--care-bg-surface)] border-t border-[color:var(--care-border-subtle)]">
                              <h4 className="ui-titulo mb-4" data-tamanho="sm">Respostas Completas:</h4>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div>
                                  <p className="ui-rotulo mb-1">Renda Mensal</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.renda || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Aporte Mensal</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.aporte || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Patrimônio</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.patrimonio || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Deseja Investir</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.deseja_investir || '-'}</p>
                                </div>
                              </div>
                              {lead.all_answers && Object.keys(lead.all_answers).length > 0 && (
                                <div className="mt-4 pt-4 border-t border-[color:var(--care-border-subtle)]">
                                  <p className="ui-rotulo mb-2">Todas as Respostas:</p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {Object.entries(lead.all_answers).map(([key, value]) => (
                                      <div key={key} className="ui-fato" style={FATO_PILHA}>
                                        <span className="ui-fato__rotulo font-mono">{key.substring(0, 8)}…</span>
                                        <span className="ui-fato__valor font-normal">{value}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Tabela de Leads - Curso */}
            {selectedType === 'curso' && cursoData && (
              <div className="ui-card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
                  <h3 className="ui-titulo" data-tamanho="sm">
                    Todos os Leads
                    <span className="text-[color:var(--care-text-tertiary)] font-normal ml-2 tabular-nums lining-nums">({cursoData?.total_leads || 0} leads)</span>
                  </h3>
                </div>

                <div className="divide-y divide-[color:var(--border)] max-h-[600px] overflow-y-auto">
                  {(() => {
                    const leadsFiltrados = cursoData?.leads || [];

                    if (leadsFiltrados.length === 0) {
                      return (
                        <p className="ui-vazio text-center py-8">
                          Nenhum lead encontrado
                        </p>
                      );
                    }

                    return leadsFiltrados.map((lead) => {
                      // Curso não tem response_id no backend — usa submitted_at+email como identidade estável
                      const leadKey = `curso-${lead.response_id || `${lead.submitted_at}-${lead.email}`}`;
                      const isExpanded = expandedLeads.has(leadKey);
                      const alternar = () => {
                        const newExpanded = new Set(expandedLeads);
                        if (isExpanded) {
                          newExpanded.delete(leadKey);
                        } else {
                          newExpanded.add(leadKey);
                        }
                        setExpandedLeads(newExpanded);
                      };
                      return (
                        <div key={leadKey}>
                          <div className="ui-linha py-3 gap-4" data-interativa onClick={alternar}>
                            <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-3">
                              <div>
                                <p className="ui-rotulo">Data</p>
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] tabular-nums lining-nums">
                                  {formatDateTime(lead.submitted_at)}
                                </p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Nome</p>
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] truncate">{lead.name || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Email</p>
                                <p className="text-[15px] text-[color:var(--care-text-secondary)] truncate">{lead.email || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Telefone</p>
                                <p className="text-[15px] text-[color:var(--care-text-secondary)] tabular-nums lining-nums">{lead.phone || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Interesse</p>
                                <span className="ui-pilula" data-tom={lead.interesse === 'Sim' ? 'positivo' : 'forte'}>
                                  {lead.interesse || '-'}
                                </span>
                              </div>
                            </div>
                            <BotaoExpandir aberto={isExpanded} controla={`lead-${leadKey}`} onAlternar={alternar} />
                          </div>

                          {isExpanded && (
                            <div id={`lead-${leadKey}`} className="p-6 bg-[var(--care-bg-surface)] border-t border-[color:var(--care-border-subtle)]">
                              <h4 className="ui-titulo mb-4" data-tamanho="sm">Respostas Completas:</h4>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                <div>
                                  <p className="ui-rotulo mb-1">Nome</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.name || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Email</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)] break-all">{lead.email || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Telefone</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)] tabular-nums lining-nums">{lead.phone || '-'}</p>
                                </div>
                                <div className="col-span-2 sm:col-span-3">
                                  <p className="ui-rotulo mb-1">Objetivo</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.objetivo || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Interesse</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.interesse || '-'}</p>
                                </div>
                              </div>
                              {lead.all_answers && Object.keys(lead.all_answers).length > 0 && (
                                <div className="mt-4 pt-4 border-t border-[color:var(--care-border-subtle)]">
                                  <p className="ui-rotulo mb-2">Todas as Respostas:</p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    {Object.entries(lead.all_answers).map(([key, value]) => (
                                      <div key={key} className="ui-fato" style={FATO_PILHA}>
                                        <span className="ui-fato__rotulo font-mono">{key.substring(0, 8)}…</span>
                                        <span className="ui-fato__valor font-normal">{value}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Tabela de Leads - Suíça */}
            {selectedType === 'suica' && suicaData && (
              <div className="ui-card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
                  <div className="ui-segmentado" role="group" aria-label="Filtrar leads">
                    <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={suicaFilter === 'qualificados'} onClick={() => setSuicaFilter('qualificados')}>
                      <span aria-hidden="true">✓</span> Qualificados (<span className="tabular-nums lining-nums">{suicaData?.leads_qualificados || 0}</span>)
                    </button>
                    <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={suicaFilter === 'nao_qualificados'} onClick={() => setSuicaFilter('nao_qualificados')}>
                      <span aria-hidden="true">✗</span> Não Qualif. (<span className="tabular-nums lining-nums">{suicaData?.leads_nao_qualificados || 0}</span>)
                    </button>
                    <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={suicaFilter === 'todos'} onClick={() => setSuicaFilter('todos')}>
                      Todos (<span className="tabular-nums lining-nums">{suicaData?.total_leads || 0}</span>)
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-[color:var(--border)] max-h-[600px] overflow-y-auto">
                  {(() => {
                    let leadsFiltrados = suicaData?.leads || [];

                    if (suicaFilter === 'qualificados') {
                      leadsFiltrados = leadsFiltrados.filter(l => l.qualificado);
                    } else if (suicaFilter === 'nao_qualificados') {
                      leadsFiltrados = leadsFiltrados.filter(l => !l.qualificado);
                    }

                    if (leadsFiltrados.length === 0) {
                      return (
                        <p className="ui-vazio text-center py-8">
                          Nenhum lead encontrado
                        </p>
                      );
                    }

                    return leadsFiltrados.map((lead) => {
                      // Suíça não tem response_id no backend — usa submitted_at+email como identidade estável
                      const leadKey = `suica-${lead.response_id || `${lead.submitted_at}-${lead.email}`}`;
                      const isExpanded = expandedLeads.has(leadKey);
                      const alternar = () => {
                        const newExpanded = new Set(expandedLeads);
                        if (isExpanded) {
                          newExpanded.delete(leadKey);
                        } else {
                          newExpanded.add(leadKey);
                        }
                        setExpandedLeads(newExpanded);
                      };
                      return (
                        <div key={leadKey}>
                          <div className="ui-linha py-3 gap-4" data-interativa onClick={alternar}>
                            <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-3">
                              <div>
                                <p className="ui-rotulo">Data</p>
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] tabular-nums lining-nums">
                                  {formatDateTime(lead.submitted_at)}
                                </p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Nome</p>
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] truncate">{lead.name || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Depósito Inicial</p>
                                <p className="text-[15px] text-[color:var(--care-text-secondary)] truncate">{lead.deposito_inicial || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Patrimônio</p>
                                <p className="text-[15px] text-[color:var(--care-text-secondary)] truncate">{lead.patrimonio_total || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Status</p>
                                <span className="ui-pilula" data-tom={lead.qualificado ? 'positivo' : 'negativo'}>
                                  <span aria-hidden="true">{lead.qualificado ? '✓' : '✗'}</span> {lead.qualificado ? 'Qualificado' : 'Não Qualif.'}
                                </span>
                              </div>
                            </div>
                            <BotaoExpandir aberto={isExpanded} controla={`lead-${leadKey}`} onAlternar={alternar} />
                          </div>

                          {isExpanded && (
                            <div id={`lead-${leadKey}`} className="p-6 bg-[var(--care-bg-surface)] border-t border-[color:var(--care-border-subtle)]">
                              <h4 className="ui-titulo mb-4" data-tamanho="sm">Dados do Lead:</h4>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div>
                                  <p className="ui-rotulo mb-1">Nome Completo</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.name || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Email</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)] break-all">{lead.email || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Telefone</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)] tabular-nums lining-nums">{lead.phone || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Nacionalidade</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.nacionalidade || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">CPF/Tax ID</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.cpf || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Moeda Preferida</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.moeda_preferida || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Estado Civil</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.estado_civil || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Situação Profissional</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.situacao_profissional || '-'}</p>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-[color:var(--care-border-subtle)]">
                                <div>
                                  <p className="ui-rotulo mb-1">Depósito Inicial</p>
                                  <p className="text-[15px] font-medium text-[color:var(--care-text-primary)]">{lead.deposito_inicial || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Patrimônio Total</p>
                                  <p className="text-[15px] font-medium text-[color:var(--care-text-primary)]">{lead.patrimonio_total || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Origem dos Recursos</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.origem_recursos || '-'}</p>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-[color:var(--care-border-subtle)]">
                                <div>
                                  <p className="ui-rotulo mb-1">Profissão</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.profissao || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Empresa</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.empresa || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Bancos</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.bancos || '-'}</p>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-[color:var(--care-border-subtle)]">
                                <div>
                                  <p className="ui-rotulo mb-1">É Pessoa Politicamente Exposta (PEP)?</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.pep || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Conexão com PEP?</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.pep_conexao || '-'}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Tabela de Leads - Consórcio */}
            {selectedType === 'consorcio' && consorcioData && (
              <div className="ui-card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-3">
                  <h3 className="ui-titulo" data-tamanho="sm">
                    Leads de Consórcio
                    <span className="text-[color:var(--care-text-tertiary)] font-normal ml-2 tabular-nums lining-nums">({consorcioData?.total_leads || 0} leads)</span>
                    <span className="text-[color:var(--care-text-tertiary)] font-normal ml-2 tabular-nums lining-nums">· {consorcioData?.leads_com_arquivos || 0} com arquivos</span>
                  </h3>
                </div>

                <div className="divide-y divide-[color:var(--border)] max-h-[600px] overflow-y-auto">
                  {(() => {
                    const leadsFiltrados = consorcioData?.leads || [];

                    if (leadsFiltrados.length === 0) {
                      return (
                        <p className="ui-vazio text-center py-8">
                          Nenhum lead encontrado
                        </p>
                      );
                    }

                    return leadsFiltrados.map((lead) => {
                      const chave = `consorcio-${lead.response_id}`;
                      const isExpanded = expandedLeads.has(chave);
                      const alternar = () => {
                        const newExpanded = new Set(expandedLeads);
                        if (isExpanded) {
                          newExpanded.delete(chave);
                        } else {
                          newExpanded.add(chave);
                        }
                        setExpandedLeads(newExpanded);
                      };
                      return (
                        <div key={lead.response_id}>
                          <div className="ui-linha py-3 gap-4" data-interativa onClick={alternar}>
                            <div className="flex-1 grid grid-cols-2 sm:grid-cols-5 gap-3">
                              <div>
                                <p className="ui-rotulo">Data</p>
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] tabular-nums lining-nums">
                                  {formatDateTime(lead.submitted_at)}
                                </p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Nome</p>
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] truncate">{lead.name || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Email</p>
                                <p className="text-[15px] text-[color:var(--care-text-secondary)] truncate">{lead.email || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Como Conheceu</p>
                                <p className="text-[15px] text-[color:var(--care-text-secondary)]">{lead.como_conheceu || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Arquivos</p>
                                <span className="ui-pilula" data-tom={lead.num_arquivos > 0 ? 'positivo' : 'forte'}>
                                  <span className="tabular-nums lining-nums">{lead.num_arquivos || 0}</span> arquivo(s)
                                </span>
                              </div>
                            </div>
                            <BotaoExpandir aberto={isExpanded} controla={`lead-${chave}`} onAlternar={alternar} />
                          </div>

                          {isExpanded && (
                            <div id={`lead-${chave}`} className="p-6 bg-[var(--care-bg-surface)] border-t border-[color:var(--care-border-subtle)]">
                              <h4 className="ui-titulo mb-4" data-tamanho="sm">Dados do Lead:</h4>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                <div>
                                  <p className="ui-rotulo mb-1">Nome Completo</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.name || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Email</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)] break-all">{lead.email || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Telefone</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)] tabular-nums lining-nums">{lead.phone || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Profissão</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.profissao || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Como Conheceu</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.como_conheceu || '-'}</p>
                                </div>
                                <div className="col-span-2 sm:col-span-3">
                                  <p className="ui-rotulo mb-1">Motivo do Consórcio</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.motivo_consorcio || '-'}</p>
                                </div>
                              </div>
                              
                              {lead.arquivos && lead.arquivos.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-[color:var(--care-border-subtle)]">
                                  <p className="ui-rotulo mb-2">Arquivos Anexados (<span className="tabular-nums lining-nums">{lead.arquivos.length}</span>):</p>
                                  <div className="space-y-2">
                                    {lead.arquivos.map((url, fileIdx) => (
                                      <a
                                        key={fileIdx}
                                        href={safeUrl(url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="ui-link flex w-fit"
                                        aria-label={`Arquivo ${fileIdx + 1} (abre em nova aba)`}
                                      >
                                        <Paperclip size={16} weight="bold" aria-hidden="true" />
                                        <span>Arquivo {fileIdx + 1}</span>
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Tabela de Leads - Anúncio Meta */}
            {selectedType === 'anuncio-meta' && anuncioMetaData && (
              <div className="ui-card">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex flex-wrap gap-2">
                    {/* Filtro por Qualificação */}
                    <div className="ui-segmentado overflow-x-auto max-w-full" role="group" aria-label="Filtrar leads">
                      <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={aplicacaoFilter === 'qualificados'} onClick={() => setAplicacaoFilter('qualificados')}>
                        <span aria-hidden="true">✓</span> Qualif. (<span className="tabular-nums lining-nums">{anuncioMetaData?.leads_qualificados || 0}</span>)
                      </button>
                      <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={aplicacaoFilter === 'desqualificados'} onClick={() => setAplicacaoFilter('desqualificados')}>
                        <span aria-hidden="true">✗</span> Desq. (<span className="tabular-nums lining-nums">{anuncioMetaData?.leads_nao_qualificados || 0}</span>)
                      </button>
                      <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={aplicacaoFilter === 'todos'} onClick={() => setAplicacaoFilter('todos')}>
                        Todos (<span className="tabular-nums lining-nums">{anuncioMetaData?.total_leads || 0}</span>)
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mensagem de feedback de e-mail */}
                {emailMessage && (
                  <div
                    role={emailMessage.type === 'success' ? 'status' : 'alert'}
                    className="ui-aviso"
                    data-tom={emailMessage.type === 'success' ? 'positivo' : 'negativo'}
                  >
                    {emailMessage.type === 'success'
                      ? <CheckCircle size={16} weight="bold" aria-hidden="true" />
                      : <WarningCircle size={16} weight="bold" aria-hidden="true" />}
                    <span>{emailMessage.text}</span>
                  </div>
                )}

                <div className="divide-y divide-[color:var(--border)]">
                  {(() => {
                    let leadsFiltrados = anuncioMetaData?.leads || [];

                    if (aplicacaoFilter === 'qualificados') {
                      leadsFiltrados = leadsFiltrados.filter(l => l.qualificado === true);
                    } else if (aplicacaoFilter === 'desqualificados') {
                      leadsFiltrados = leadsFiltrados.filter(l => l.qualificado === false);
                    }

                    if (leadsFiltrados.length === 0) {
                      return (
                        <p className="ui-vazio text-center py-8">
                          Nenhum lead encontrado
                        </p>
                      );
                    }

                    return leadsFiltrados.map((lead) => {
                      const isExpanded = expandedLeads.has(lead.response_id);
                      const painelId = `lead-meta-${lead.response_id}`;
                      const enviado = !!lead.contato_realizado;
                      const enviando = !!sendingEmail[lead.response_id];
                      const botaoEnvio = (extra) => (
                        <button
                          type="button"
                          className={`ui-botao${extra ? ` ${extra}` : ''}`}
                          data-tamanho="sm"
                          data-so-icone
                          data-variante={enviado ? 'fio' : 'solido'}
                          onClick={(e) => { e.stopPropagation(); enviarEmailConsultorAnuncioMeta(lead.response_id, lead.consultor_responsavel); }}
                          aria-label={enviado ? 'Reenviar e-mail ao consultor (já enviado)' : 'Enviar e-mail ao consultor'}
                          aria-busy={enviando || undefined}
                          disabled={!lead.consultor_responsavel || sendingEmail[lead.response_id]}
                          title={enviado ? 'E-mail já enviado' : 'Enviar e-mail para o consultor'}
                        >
                          <IconeEnvio enviando={enviando} enviado={enviado} />
                        </button>
                      );
                      const pilulaStatus = (curta) => (
                        <span className="ui-pilula" data-tom={lead.qualificado ? 'positivo' : 'negativo'}>
                          <span aria-hidden="true">{lead.qualificado ? '✓' : '✗'}</span>
                          {curta
                            ? <span className="sr-only">{lead.qualificado ? 'Qualificado' : 'Desqualificado'}</span>
                            : <> {lead.qualificado ? 'Qualif.' : 'Desq.'}</>}
                        </span>
                      );
                      return (
                        <div key={lead.response_id}>
                          {/* Desktop Layout */}
                          <div
                            className="ui-linha hidden sm:flex py-3 gap-4"
                            data-interativa
                            onClick={() => toggleLead(lead.response_id)}
                          >
                            <div className="flex-1 grid grid-cols-9 gap-2 items-center">
                              <div>
                                <p className="ui-rotulo">Data</p>
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] tabular-nums lining-nums">
                                  {formatDateTime(lead.submitted_at)}
                                </p>
                              </div>
                              <div className="col-span-2">
                                <p className="ui-rotulo">Nome</p>
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] truncate">{lead.name || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Telefone</p>
                                <p className="text-xs text-[color:var(--care-text-secondary)] tabular-nums lining-nums">{lead.phone || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Origem</p>
                                <span className="ui-pilula" data-tom="acento">Anúncio</span>
                              </div>
                              <div>
                                <p className="ui-rotulo">Status</p>
                                {pilulaStatus(false)}
                              </div>
                              <div className="col-span-2" onClick={(e) => e.stopPropagation()}>
                                <p className="ui-rotulo">Consultor</p>
                                <div className="flex items-center gap-1 mt-0.5">
                                  <select
                                    value={lead.consultor_responsavel || ''}
                                    onChange={(e) => atualizarConsultorAnuncioMeta(lead.response_id, e.target.value)}
                                    className="ui-campo flex-1 min-w-0"
                                    data-tamanho="sm"
                                    aria-label="Consultor responsável"
                                  >
                                    {renderConsultorOptions('Selecionar')}
                                  </select>
                                  {botaoEnvio('shrink-0')}
                                </div>
                              </div>
                              <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
                                <label className={CLS_LABEL_CHECK}>
                                  <input
                                    type="checkbox"
                                    checked={lead.contato_realizado || false}
                                    onChange={(e) => marcarContatoAnuncioMeta(lead.response_id, e.target.checked)}
                                    className={CLS_CHECKBOX}
                                  />
                                  Contato
                                </label>
                                <label className={CLS_LABEL_CHECK}>
                                  <input
                                    type="checkbox"
                                    checked={lead.reuniao_realizada || false}
                                    onChange={(e) => marcarReuniaoAnuncioMeta(lead.response_id, e.target.checked)}
                                    className={CLS_CHECKBOX}
                                  />
                                  Reunião
                                </label>
                              </div>
                            </div>
                            <BotaoExpandir aberto={isExpanded} controla={painelId} onAlternar={() => toggleLead(lead.response_id)} />
                          </div>

                          {/* Mobile Layout */}
                          <div
                            className={`sm:hidden ${CLS_LINHA_BLOCO}`}
                            onClick={() => toggleLead(lead.response_id)}
                          >
                            <div className="flex justify-between items-start gap-2 mb-2">
                              <div className="min-w-0">
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)]">{lead.name || '-'}</p>
                                <p className="text-xs text-[color:var(--care-text-secondary)] tabular-nums lining-nums">{formatDateTime(lead.submitted_at)}</p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="ui-pilula" data-tom="acento">Anúncio</span>
                                {pilulaStatus(true)}
                                <BotaoExpandir aberto={isExpanded} controla={painelId} onAlternar={() => toggleLead(lead.response_id)} />
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-[color:var(--care-text-secondary)] tabular-nums lining-nums">
                              <span>{lead.phone || '-'}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-3 mt-2 pt-2 border-t border-[color:var(--care-border-subtle)]" onClick={(e) => e.stopPropagation()}>
                              <select
                                value={lead.consultor_responsavel || ''}
                                onChange={(e) => atualizarConsultorAnuncioMeta(lead.response_id, e.target.value)}
                                className="ui-campo basis-full"
                                data-tamanho="sm"
                                aria-label="Consultor responsável"
                              >
                                {renderConsultorOptions('Consultor')}
                              </select>
                              {botaoEnvio()}
                              <label className={CLS_LABEL_CHECK}>
                                <input
                                  type="checkbox"
                                  checked={lead.contato_realizado || false}
                                  onChange={(e) => marcarContatoAnuncioMeta(lead.response_id, e.target.checked)}
                                  className={CLS_CHECKBOX}
                                />
                                Contato
                              </label>
                              <label className={CLS_LABEL_CHECK}>
                                <input
                                  type="checkbox"
                                  checked={lead.reuniao_realizada || false}
                                  onChange={(e) => marcarReuniaoAnuncioMeta(lead.response_id, e.target.checked)}
                                  className={CLS_CHECKBOX}
                                />
                                Reunião
                              </label>
                            </div>
                          </div>

                          {/* Expanded Content */}
                          {isExpanded && (
                            <div id={painelId} className="p-4 sm:p-6 bg-[var(--care-bg-surface)] border-t border-[color:var(--care-border-subtle)]">
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                <div>
                                  <p className="ui-rotulo mb-1">Nome Completo</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.name || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Email</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)] break-all">{lead.email || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Telefone</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)] tabular-nums lining-nums">{lead.phone || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Qualificado</p>
                                  <p className={`text-[15px] font-medium ${lead.qualificado ? 'text-[color:var(--care-positive-text)]' : 'text-[color:var(--care-danger-text)]'}`}>
                                    {lead.qualificado ? 'Sim' : 'Não'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            {/* Barras de Proporção - Qualificados vs Desqualificados por Origem */}
            {selectedType === 'aplicacao' && aplicacaoData && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8" data-testid="proportion-bars-section">
                {[
                  { id: 'organico', titulo: 'Orgânico', leads: aplicacaoData?.leads?.filter(l => l.origem !== 'Anúncio') || [], atrasoQ: 0, atrasoD: 0.08 },
                  { id: 'anuncio', titulo: 'Anúncio', leads: aplicacaoData?.leads?.filter(l => l.origem === 'Anúncio') || [], atrasoQ: 0.04, atrasoD: 0.12 },
                ].map(({ id, titulo, leads, atrasoQ, atrasoD }) => {
                  const qualificados = leads.filter(l => l.qualificado).length;
                  const desqualificados = leads.length - qualificados;
                  const reunioes = leads.filter(l => l.reuniao_agendada).length;
                  const percQualif = leads.length > 0 ? (qualificados / leads.length * 100) : 0;
                  const percDesqual = leads.length > 0 ? (desqualificados / leads.length * 100) : 0;
                  const percReunioes = leads.length > 0 ? (reunioes / leads.length * 100) : 0;
                  return (
                    // Barra de proporção (Orgânico / Anúncio): divergente rust ↔ teal (DS v3 §2.6).
                    // O cartão entra parado (a subida de 20px só decorava); só a barra cresce, porque mostra o dado.
                    <div key={id} className="ui-card" data-testid={`proportion-bar-${id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[15px] font-medium text-[color:var(--care-text-secondary)]">{titulo}</span>
                        <span className="ui-pilula" data-tom="forte">
                          <span className="tabular-nums lining-nums">{leads.length}</span> leads
                        </span>
                      </div>
                      <div className="flex h-4 rounded-sm overflow-hidden bg-[var(--care-bg-secondary)]">
                        {percQualif > 0 && (
                          <motion.div
                            className="bg-[var(--care-chart-div-pos-2)] flex items-center justify-center"
                            initial={{ width: 0 }}
                            animate={{ width: `${percQualif}%` }}
                            transition={{ delay: atrasoQ, duration: 0.28, ease: [0.2, 0, 0, 1] }}
                            title={`${qualificados} qualificados (${percQualif.toFixed(1).replace('.', ',')}%)`}
                          >
                            {percQualif > 20 && <span className="text-[11px] font-semibold text-[color:var(--care-text-inverse)] tabular-nums lining-nums">{percQualif.toFixed(0)}%</span>}
                          </motion.div>
                        )}
                        {percDesqual > 0 && (
                          <motion.div
                            className="bg-[var(--care-chart-div-neg-2)] flex items-center justify-center"
                            initial={{ width: 0 }}
                            animate={{ width: `${percDesqual}%` }}
                            transition={{ delay: atrasoD, duration: 0.28, ease: [0.2, 0, 0, 1] }}
                            title={`${desqualificados} desqualificados (${percDesqual.toFixed(1).replace('.', ',')}%)`}
                          >
                            {percDesqual > 20 && <span className="text-[11px] font-semibold text-[color:var(--care-text-inverse)] tabular-nums lining-nums">{percDesqual.toFixed(0)}%</span>}
                          </motion.div>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-xs tabular-nums lining-nums">
                        <span className="text-[color:var(--care-positive-text)] font-medium" data-testid={`${id}-qualificados`}>
                          Qualif. {qualificados} ({percQualif.toFixed(0)}%)
                        </span>
                        <span className="text-[color:var(--care-danger-text)] font-medium" data-testid={`${id}-desqualificados`}>
                          Desq. {desqualificados} ({percDesqual.toFixed(0)}%)
                        </span>
                      </div>
                      <div className="flex items-center gap-2 pt-3 border-t border-[color:var(--care-border-subtle)] text-[color:var(--care-text-tertiary)]">
                        <CalendarBlank size={16} weight="bold" aria-hidden="true" />
                        <span className="text-xs text-[color:var(--care-text-secondary)] tabular-nums lining-nums">
                          <span className="font-semibold text-[color:var(--care-accent-text)]">{reunioes}</span> {reunioes === 1 ? 'reunião' : 'reuniões'}
                          <span className="text-[color:var(--care-text-tertiary)] ml-1">({percReunioes.toFixed(1).replace('.', ',')}%)</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Aplicação Qualified Leads Table - Only show when Aplicação is selected */}
            {selectedType === 'aplicacao' && aplicacaoData && (
              <div className="ui-card mt-8">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-3">
                  <div className="flex-1 min-w-0">
                    {/* Filtros (controles segmentados) - quebram em múltiplas linhas */}
                    <div className="flex flex-wrap gap-2">
                      {/* Filtro por Qualificação */}
                      <div className="ui-segmentado overflow-x-auto max-w-full" role="group" aria-label="Qualificação">
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={aplicacaoFilter === 'qualificados'} onClick={() => setAplicacaoFilter('qualificados')}>
                            <span aria-hidden="true">✓</span> Qualif. (<span className="tabular-nums lining-nums">{aplicacaoData?.qualificados || 0}</span>)
                          </button>
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={aplicacaoFilter === 'desqualificados'} onClick={() => setAplicacaoFilter('desqualificados')}>
                            <span aria-hidden="true">✗</span> Desq. (<span className="tabular-nums lining-nums">{aplicacaoData?.nao_qualificados || 0}</span>)
                          </button>
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={aplicacaoFilter === 'todos'} onClick={() => setAplicacaoFilter('todos')}>
                            Todos (<span className="tabular-nums lining-nums">{aplicacaoData?.total_leads || 0}</span>)
                          </button>
                      </div>
                      {/* Filtro por Origem */}
                      <div className="ui-segmentado overflow-x-auto max-w-full" role="group" aria-label="Origem">
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={origemFilter === 'todos'} onClick={() => setOrigemFilter('todos')}>
                            Todos (<span className="tabular-nums lining-nums">{aplicacaoData?.total_leads || 0}</span>)
                          </button>
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={origemFilter === 'organico'} onClick={() => setOrigemFilter('organico')}>
                            Orgânico (<span className="tabular-nums lining-nums">{aplicacaoData?.leads_organicos || 0}</span>)
                          </button>
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={origemFilter === 'anuncio'} onClick={() => setOrigemFilter('anuncio')}>
                            Anúncio (<span className="tabular-nums lining-nums">{aplicacaoData?.leads_anuncio || 0}</span>)
                          </button>
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={origemFilter === 'medicos'} onClick={() => setOrigemFilter('medicos')}>
                            Médicos (<span className="tabular-nums lining-nums">{(aplicacaoData?.leads || []).filter(l => l.origem === 'Médicos').length}</span>)
                          </button>
                      </div>
                      {/* Filtro por Consultor (tag) */}
                      <div className="ui-segmentado overflow-x-auto max-w-full" role="group" aria-label="Consultor">
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={consultorTagFilter === 'todos'} onClick={() => setConsultorTagFilter('todos')}>
                            Todos consultores
                          </button>
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={consultorTagFilter === 'CL'} onClick={() => setConsultorTagFilter('CL')}>
                            CL (<span className="tabular-nums lining-nums">{(aplicacaoData?.leads || []).filter(l => (l.consultor_tag || 'CL') === 'CL').length}</span>)
                          </button>
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={consultorTagFilter === 'MH'} onClick={() => setConsultorTagFilter('MH')}>
                            MH (<span className="tabular-nums lining-nums">{(aplicacaoData?.leads || []).filter(l => l.consultor_tag === 'MH').length}</span>)
                          </button>
                      </div>
                      {/* Filtro por Reunião */}
                      <div className="ui-segmentado overflow-x-auto max-w-full" role="group" aria-label="Reunião">
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={reuniaoFilter === 'todos'} onClick={() => setReuniaoFilter('todos')}>
                            Todas reuniões
                          </button>
                          <button type="button" className="ui-segmentado__opcao whitespace-nowrap" aria-pressed={reuniaoFilter === 'sem_reuniao'} onClick={() => setReuniaoFilter('sem_reuniao')}>
                            Sem reunião (<span className="tabular-nums lining-nums">{(aplicacaoData?.leads || []).filter(l => !l.reuniao_agendada && !l.reuniao_realizada).length}</span>)
                          </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      className="ui-botao"
                      data-variante="solido"
                      onClick={syncGoogleSheets}
                      disabled={syncingSheets}
                      aria-busy={syncingSheets || undefined}
                    >
                      <ArrowClockwise size={16} weight="bold" aria-hidden="true" className={syncingSheets ? 'motion-safe:animate-spin' : undefined} />
                      <span className="hidden sm:inline">{syncingSheets ? 'Sincronizando...' : 'Sync Ads'}</span>
                      <span className="sm:hidden">Ads</span>
                    </button>
                    <button
                      type="button"
                      className="ui-botao"
                      data-variante="fio"
                      onClick={downloadExcel}
                    >
                      <DownloadSimple size={16} weight="bold" aria-hidden="true" />
                      <span className="hidden sm:inline">Baixar Excel</span>
                      <span className="sm:hidden">Excel</span>
                    </button>
                  </div>
                </div>

                {/* Disclaimer da fórmula de qualificação */}
                <p className="ui-meta">
                  <span className="font-medium text-[color:var(--care-text-secondary)]">Critério de qualificação:</span> Patrimônio <strong className="font-semibold text-[color:var(--care-text-primary)] tabular-nums lining-nums">≥ R$ 500.000</strong> OU (Renda <strong className="font-semibold text-[color:var(--care-text-primary)] tabular-nums lining-nums">≥ R$ 15.000</strong> E Poupança <strong className="font-semibold text-[color:var(--care-text-primary)] tabular-nums lining-nums">&gt; R$ 10.000</strong>)
                </p>

                {/* Mensagem de feedback de e-mail ou sincronização */}
                {(emailMessage || syncMessage) && (() => {
                  const tipoMsg = emailMessage?.type || syncMessage?.type;
                  return (
                    <div role="status" className="ui-aviso" data-tom={tipoMsg === 'warning' ? 'atencao' : tipoMsg === 'success' ? 'positivo' : 'negativo'}>
                      {tipoMsg === 'warning'
                        ? <Warning size={16} weight="bold" aria-hidden="true" />
                        : tipoMsg === 'success'
                          ? <CheckCircle size={16} weight="bold" aria-hidden="true" />
                          : <WarningCircle size={16} weight="bold" aria-hidden="true" />}
                      <span>{emailMessage?.text || syncMessage?.text}</span>
                    </div>
                  );
                })()}

                <div className="relative text-[color:var(--care-text-tertiary)]">
                  <MagnifyingGlass size={16} weight="bold" aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Buscar lead por nome..."
                    aria-label="Buscar lead por nome"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="ui-campo w-full pl-10"
                  />
                </div>

                <div className="divide-y divide-[color:var(--border)]">
                  {(() => {
                    // Mesmo resultado (memo) que alimenta o gráfico: filtros + busca adiada.
                    const leadsFiltrados = aplicacaoLeadsFiltrados;

                    if (leadsFiltrados.length === 0) {
                      // Vazio por causa da busca/filtros: diz isso e oferece a saída.
                      if (searchDeferido || origemFilter !== 'todos' || consultorTagFilter !== 'todos' || reuniaoFilter !== 'todos') {
                        return (
                          <div className="ui-vazio-bloco items-center text-center py-8">
                            <p className="ui-vazio">Nenhum lead com esses filtros.</p>
                            <button
                              type="button"
                              className="ui-vazio__caminho"
                              onClick={() => { setSearchTerm(''); setOrigemFilter('todos'); setConsultorTagFilter('todos'); setReuniaoFilter('todos'); }}
                            >
                              Limpar filtros <ArrowRight size={16} weight="bold" aria-hidden="true" />
                            </button>
                          </div>
                        );
                      }
                      return (
                        <p className="ui-vazio text-center py-8">
                          {aplicacaoFilter === 'qualificados' && 'Nenhum lead qualificado ainda'}
                          {aplicacaoFilter === 'desqualificados' && 'Nenhum lead desqualificado'}
                          {aplicacaoFilter === 'todos' && 'Nenhum lead ainda'}
                        </p>
                      );
                    }

                    return leadsFiltrados.map((lead) => {
                      const isExpanded = expandedLeads.has(lead.response_id);
                      const enviado = !!lead.contato_realizado;
                      const enviando = !!sendingEmail[lead.response_id];
                      const medico = lead.origem === 'Médicos' || lead.qualificado == null;
                      const nomeLead = lead.nome || `${lead.respostas?.oM9hIYlRwMQ9 || ''} ${lead.respostas?.DPIy4hvUaGat || ''}`.trim() || '-';
                      const tagConsultor = (lead.consultor_tag === 'MH' || lead.consultor_tag === 'CL' || lead.consultor_tag === 'Site') && (
                        <span
                          className="ui-pilula shrink-0"
                          data-tom={lead.consultor_tag === 'CL' ? 'acento' : lead.consultor_tag === 'MH' ? 'forte' : undefined}
                          title={lead.consultor_tag === 'MH' ? 'Origem: Martin Hauser' : lead.consultor_tag === 'Site' ? 'Origem: site caremfo.com' : 'Origem: Conrado'}
                        >
                          {lead.consultor_tag}
                        </span>
                      );
                      const pilulaOrigem = (
                        <span className="ui-pilula" data-tom={lead.origem === 'Anúncio' ? 'acento' : undefined}>
                          {lead.origem || 'Orgânico'}
                        </span>
                      );
                      const botaoEnvio = (
                        <button
                          type="button"
                          className="ui-botao"
                          data-tamanho="sm"
                          data-so-icone
                          data-variante={enviado ? 'fio' : 'solido'}
                          onClick={(e) => { e.stopPropagation(); enviarEmailConsultor(lead.response_id, lead.consultor_responsavel); }}
                          aria-label={enviado ? 'Reenviar e-mail ao consultor (já enviado)' : 'Enviar e-mail ao consultor'}
                          aria-busy={enviando || undefined}
                          disabled={!lead.consultor_responsavel || sendingEmail[lead.response_id]}
                          title={enviado ? 'E-mail já enviado' : 'Enviar e-mail para o consultor'}
                        >
                          <IconeEnvio enviando={enviando} enviado={enviado} />
                        </button>
                      );
                      const reunioesMatch = Array.isArray(lead.reunioes_match) && lead.reunioes_match.length > 0 && (
                        <span
                          className="text-[11px] text-[color:var(--care-text-secondary)]"
                          title={lead.reunioes_match.map(m => `${m.tipo || m.name} • ${(m.happened_at||'').slice(0,10)} • ${(m.consultores||[]).join(', ')}`).join('\n')}
                        >
                          → {Array.from(new Set(lead.reunioes_match.flatMap(m => m.consultores || []))).join(', ')}
                        </span>
                      );
                      return (
                        <div key={lead.response_id}>
                          {/* Mobile Layout */}
                          <div
                            className={`sm:hidden ${CLS_LINHA_BLOCO}`}
                            onClick={() => toggleLead(lead.response_id)}
                          >
                            <div className="flex justify-between items-start gap-2 mb-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <p className="text-[15px] font-medium text-[color:var(--care-text-primary)]">
                                    {nomeLead}
                                  </p>
                                  {tagConsultor}
                                </div>
                                <p className="text-xs text-[color:var(--care-text-secondary)] tabular-nums lining-nums">
                                  {formatDateTime(lead.submitted_at)}
                                </p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {pilulaOrigem}
                                <span className="ui-pilula" data-tom={medico ? undefined : lead.qualificado ? 'positivo' : 'negativo'}>
                                  {medico ? 'Méd.' : (
                                    <>
                                      <span aria-hidden="true">{lead.qualificado ? '✓' : '✗'}</span>
                                      <span className="sr-only">{lead.qualificado ? 'Qualificado' : 'Desqualificado'}</span>
                                    </>
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-[color:var(--care-text-secondary)] tabular-nums lining-nums">
                              <span className="font-mono">{lead.cpf || lead.respostas?.tMcn0KRDKsYD || '-'}</span>
                              <span aria-hidden="true">|</span>
                              <span>{lead.telefone || lead.respostas?.['402wPlNnH4Vc'] || '-'}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 pt-2 border-t border-[color:var(--care-border-subtle)]">
                              <select
                                value={lead.consultor_responsavel || ''}
                                onChange={(e) => { e.stopPropagation(); atualizarConsultor(lead.response_id, e.target.value); }}
                                onClick={(e) => e.stopPropagation()}
                                className="ui-campo basis-full"
                                data-tamanho="sm"
                                aria-label="Consultor responsável"
                              >
                                {renderConsultorOptions('Consultor')}
                              </select>
                              {botaoEnvio}
                              <label className={CLS_LABEL_CHECK} onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={lead.contato_realizado || false}
                                  onChange={(e) => marcarContato(lead.response_id, e.target.checked)}
                                  className={CLS_CHECKBOX}
                                />
                                Contato
                              </label>
                              <span className="text-xs text-[color:var(--care-text-tertiary)]">Reunião:</span>
                              <label className={CLS_LABEL_CHECK} onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={lead.reuniao_agendada || false}
                                  onChange={(e) => marcarReuniaoAgendada(lead.response_id, e.target.checked)}
                                  className={CLS_CHECKBOX}
                                />
                                Agend.
                              </label>
                              <label className={CLS_LABEL_CHECK} onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={lead.reuniao_realizada || false}
                                  onChange={(e) => marcarReuniao(lead.response_id, e.target.checked)}
                                  className={CLS_CHECKBOX}
                                />
                                Realiz.
                              </label>
                              {reunioesMatch}
                              <BotaoExpandir aberto={isExpanded} controla={`lead-aplic-m-${lead.response_id}`} onAlternar={() => toggleLead(lead.response_id)} className="ml-auto" />
                            </div>
                          </div>

                          {/* Mobile Expanded Section */}
                          {isExpanded && (
                            <div id={`lead-aplic-m-${lead.response_id}`} className="sm:hidden p-4 bg-[var(--care-bg-surface)] border-t border-[color:var(--care-border-subtle)]">
                              <h4 className="ui-titulo mb-3" data-tamanho="sm">Respostas:</h4>
                              <div className="grid grid-cols-2 gap-3 text-[15px]">
                                <div>
                                  <p className="ui-rotulo">Email</p>
                                  <p className="text-[color:var(--care-text-primary)] break-all">{lead.email || lead.respostas?.RFVP931C33mV || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo">Telefone</p>
                                  <p className="text-[color:var(--care-text-primary)] tabular-nums lining-nums">{lead.telefone || lead.respostas?.['402wPlNnH4Vc'] || '-'}</p>
                                </div>
                                <div className="col-span-2">
                                  <p className="ui-rotulo">Renda Mensal</p>
                                  <p className="text-[color:var(--care-text-primary)]">{lead.renda_mensal || getLeadFieldValue(lead, 'renda')}</p>
                                </div>
                                <div className="col-span-2">
                                  <p className="ui-rotulo">Patrimônio</p>
                                  <p className="text-[color:var(--care-text-primary)]">{lead.patrimonio || getLeadFieldValue(lead, 'patrimonio')}</p>
                                </div>
                                <div className="col-span-2">
                                  <p className="ui-rotulo">Capacidade de Investimento</p>
                                  <p className="text-[color:var(--care-text-primary)]">{lead.capacidade_poupanca || getLeadFieldValue(lead, 'aporte')}</p>
                                </div>
                                <div className="col-span-2 flex justify-end">
                                  <button
                                    type="button"
                                    className="ui-botao"
                                    data-variante="fio"
                                    data-tom="negativo"
                                    data-tamanho="sm"
                                    onClick={(e) => abrirApagarLead(e, lead)}
                                  >
                                    <Trash size={16} weight="bold" aria-hidden="true" /> Apagar lead
                                  </button>
                                </div>
                                <div className="col-span-2 mt-2 pt-2 border-t border-[color:var(--care-border-subtle)]">
                                  <label htmlFor={`obs-m-${lead.response_id}`} className="ui-rotulo block mb-1">Observação</label>
                                  <textarea
                                    id={`obs-m-${lead.response_id}`}
                                    defaultValue={lead.observacao || ''}
                                    onBlur={(e) => {
                                      if (e.target.value !== (lead.observacao || '')) {
                                        salvarObservacao(lead.response_id, e.target.value);
                                      }
                                    }}
                                    placeholder="Ex: já mandei mais de 7 mensagens..."
                                    rows={2}
                                    aria-invalid={erroObs[lead.response_id] || undefined}
                                    aria-describedby={erroObs[lead.response_id] ? `obs-m-erro-${lead.response_id}` : undefined}
                                    className="ui-campo ui-campo--area w-full"
                                  />
                                  {erroObs[lead.response_id] && (
                                    <p id={`obs-m-erro-${lead.response_id}`} className="ui-aviso mt-1" data-tom="negativo">
                                      <WarningCircle size={16} weight="bold" aria-hidden="true" />
                                      Não salvou. Tente de novo.
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Desktop Layout */}
                          <div
                            className={`hidden sm:flex items-center gap-4 ${CLS_LINHA_BLOCO}`}
                            onClick={() => toggleLead(lead.response_id)}
                          >
                            <div className="flex-1 grid grid-cols-7 gap-3">
                              <div>
                                <p className="ui-rotulo">Data</p>
                                <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] tabular-nums lining-nums">
                                  {formatDateTime(lead.submitted_at)}
                                </p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Nome</p>
                                <div className="flex items-center gap-1.5">
                                  <p className="text-[15px] font-medium text-[color:var(--care-text-primary)] truncate">
                                    {nomeLead}
                                  </p>
                                  {tagConsultor}
                                </div>
                              </div>
                              <div>
                                <p className="ui-rotulo">CPF</p>
                                <p className="text-[color:var(--care-text-secondary)] font-mono text-xs">{lead.cpf || lead.respostas?.tMcn0KRDKsYD || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Telefone</p>
                                <p className="text-[color:var(--care-text-secondary)] text-xs tabular-nums lining-nums">{lead.telefone || lead.respostas?.['402wPlNnH4Vc'] || '-'}</p>
                              </div>
                              <div>
                                <p className="ui-rotulo">Origem</p>
                                {pilulaOrigem}
                              </div>
                              <div>
                                <p className="ui-rotulo">Status</p>
                                <span className="ui-pilula" data-tom={medico ? undefined : lead.qualificado ? 'positivo' : 'negativo'}>
                                  {medico ? 'Médico' : (
                                    <><span aria-hidden="true">{lead.qualificado ? '✓' : '✗'}</span> {lead.qualificado ? 'Qualif.' : 'Desq.'}</>
                                  )}
                                </span>
                              </div>
                              <div onClick={(e) => e.stopPropagation()}>
                                <p className="ui-rotulo">Consultor</p>
                                <div className="flex items-center gap-1">
                                  <select
                                    value={lead.consultor_responsavel || ''}
                                    onChange={(e) => atualizarConsultor(lead.response_id, e.target.value)}
                                    className="ui-campo min-w-0"
                                    data-tamanho="sm"
                                    aria-label="Consultor responsável"
                                  >
                                    {renderConsultorOptions('Selecionar')}
                                  </select>
                                  {botaoEnvio}
                                </div>
                              </div>
                              <label className={`${CLS_LABEL_CHECK} self-center`} onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={lead.contato_realizado || false}
                                  onChange={(e) => marcarContato(lead.response_id, e.target.checked)}
                                  className={CLS_CHECKBOX}
                                />
                                Contato
                              </label>
                              <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                                <span className="text-xs text-[color:var(--care-text-tertiary)]">Reunião:</span>
                                <label className={CLS_LABEL_CHECK}>
                                  <input
                                    type="checkbox"
                                    checked={lead.reuniao_agendada || false}
                                    onChange={(e) => marcarReuniaoAgendada(lead.response_id, e.target.checked)}
                                    className={CLS_CHECKBOX}
                                  />
                                  Agendada
                                </label>
                                <label className={CLS_LABEL_CHECK}>
                                  <input
                                    type="checkbox"
                                    checked={lead.reuniao_realizada || false}
                                    onChange={(e) => marcarReuniao(lead.response_id, e.target.checked)}
                                    className={CLS_CHECKBOX}
                                  />
                                  Realizada
                                </label>
                                {reunioesMatch}
                              </div>
                            </div>
                            <BotaoExpandir aberto={isExpanded} controla={`lead-aplic-${lead.response_id}`} onAlternar={() => toggleLead(lead.response_id)} />
                          </div>

                          {isExpanded && (
                            <div id={`lead-aplic-${lead.response_id}`} className="p-6 bg-[var(--care-bg-surface)] border-t border-[color:var(--care-border-subtle)]">
                              <h4 className="ui-titulo mb-4" data-tamanho="sm">Respostas Completas:</h4>
                              <div className="grid grid-cols-3 gap-4">
                                <div className="col-span-3">
                                  <p className="ui-rotulo mb-1">Nome Completo</p>
                                  <p className="text-base font-semibold text-[color:var(--care-text-primary)]">
                                    {lead.nome || `${lead.respostas?.oM9hIYlRwMQ9 || ''} ${lead.respostas?.DPIy4hvUaGat || ''}`.trim() || '-'}
                                  </p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Email</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.email || lead.respostas?.RFVP931C33mV || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Telefone</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)] tabular-nums lining-nums">{lead.telefone || lead.respostas?.['402wPlNnH4Vc'] || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">CPF</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)] font-mono">{lead.cpf || lead.respostas?.tMcn0KRDKsYD || '-'}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Renda Mensal</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.renda_mensal || getLeadFieldValue(lead, 'renda')}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Patrimônio</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.patrimonio || getLeadFieldValue(lead, 'patrimonio')}</p>
                                </div>
                                <div>
                                  <p className="ui-rotulo mb-1">Capacidade Investimento</p>
                                  <p className="text-[15px] text-[color:var(--care-text-primary)]">{lead.capacidade_poupanca || getLeadFieldValue(lead, 'aporte')}</p>
                                </div>
                                <div className="col-start-3 justify-self-end self-end">
                                  <button
                                    type="button"
                                    className="ui-botao"
                                    data-variante="fio"
                                    data-tom="negativo"
                                    data-tamanho="sm"
                                    onClick={(e) => abrirApagarLead(e, lead)}
                                  >
                                    <Trash size={16} weight="bold" aria-hidden="true" /> Apagar lead
                                  </button>
                                </div>
                                <div className="col-span-3 mt-2 pt-3 border-t border-[color:var(--care-border-subtle)]">
                                  <label htmlFor={`obs-${lead.response_id}`} className="ui-rotulo block mb-1">Observação</label>
                                  <div className="flex gap-2">
                                    <textarea
                                      id={`obs-${lead.response_id}`}
                                      defaultValue={lead.observacao || ''}
                                      onBlur={(e) => {
                                        if (e.target.value !== (lead.observacao || '')) {
                                          salvarObservacao(lead.response_id, e.target.value);
                                        }
                                      }}
                                      placeholder="Ex: já mandei mais de 7 mensagens para esse lead..."
                                      rows={2}
                                      aria-invalid={erroObs[lead.response_id] || undefined}
                                      aria-describedby={erroObs[lead.response_id] ? `obs-erro-${lead.response_id}` : undefined}
                                      className="ui-campo ui-campo--area flex-1"
                                    />
                                    <span role="status" className="ui-meta self-end">
                                      {savingObs[lead.response_id] ? 'Salvando...' : ''}
                                    </span>
                                  </div>
                                  {erroObs[lead.response_id] && (
                                    <p id={`obs-erro-${lead.response_id}`} className="ui-aviso mt-1" data-tom="negativo">
                                      <WarningCircle size={16} weight="bold" aria-hidden="true" />
                                      Não salvou. Tente de novo.
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
            </div>
          </>
        ))}

        {/* PL BTG TAB */}
        {activeTab === 'pl' && (
          <>
            {/* AUC: loading / erro / vazio — esqueleto, aviso de erro com ação, vazio do kit */}
            {aucLoading && !aucData ? (
              <TabSkeleton label="Carregando AUC..." />
            ) : aucError && !aucData ? (
              <div className="ui-card items-start">
                <p role="alert" className="ui-aviso" data-tom="negativo">
                  <WarningCircle size={16} weight="bold" aria-hidden="true" />
                  Não foi possível carregar os dados de AUC
                </p>
                <button type="button" className="ui-botao" data-variante="fio" data-tamanho="sm" onClick={fetchAucData}>Tentar de novo</button>
              </div>
            ) : !aucData ? (
              <div className="ui-card">
                <div className="ui-vazio-bloco" data-area>
                  <p className="ui-vazio">Nenhum dado de AUC disponível ainda</p>
                  <p className="ui-vazio__dica">O BTG chega automático pela API; o XP entra por upload (no fim da aba).</p>
                </div>
              </div>
            ) : (
            <>

            {/* BTG diário (via API iaas-api-position — snapshot atualizado 1x/dia, não streaming) */}
            {aucData?.btg_daily_latest && (
              <div className="ui-card mb-6" data-testid="auc-btg-daily">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="ui-pilha" data-gap="2">
                    <span className="ui-rotulo" data-tom="forte">BTG</span>
                    <span className="ui-meta">
                      Atualizado em {new Date(aucData.btg_daily_latest.ref_date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                      {' · '}
                      API BTG (diário)
                    </span>
                  </div>
                  <div className="flex gap-6 sm:gap-8">
                    <div className="ui-kpi" data-tamanho="sm">
                      <span className="ui-rotulo">PL Total</span>
                      <span className="ui-kpi__valor">{formatCurrency(aucData.btg_daily_latest.pl_total)}</span>
                    </div>
                    <div className="ui-kpi" data-tamanho="sm">
                      <span className="ui-rotulo">Contas</span>
                      <span className="ui-kpi__valor">{aucData.btg_daily_latest.num_contas}</span>
                    </div>
                    <div className="ui-kpi hidden sm:flex" data-tamanho="sm">
                      <span className="ui-rotulo">Ticket Médio</span>
                      <span className="ui-kpi__valor">{formatCurrency(aucData.btg_daily_latest.ticket_medio)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cards Consolidados — fonte preferida: auc_daily_latest (BTG API + XP carry-forward).
                Fallback para aucData.latest (visão mensal legada) se o diário ainda não estiver disponível. */}
            {(aucData?.auc_daily_latest || aucData?.latest) && (() => {
              const d = aucData.auc_daily_latest;
              const consolidated = d ? {
                pl_total: d.pl_total,
                num_contas: d.num_contas,
                ticket_medio: d.ticket_medio,
                btg_pl: d.btg_pl,
                xp_pl: d.xp_pl,
                btg_contas: d.btg_contas,
                xp_contas: d.xp_contas,
                ref_label: new Date(d.ref_date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }),
                xp_carry_forward: d.xp_carry_forward,
              } : {
                ...aucData.latest,
                ref_label: formatMonth(aucData.latest.ref_month),
                xp_carry_forward: false,
              };
              return (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="ui-card" data-testid="auc-total">
                  <div className="ui-kpi" data-tamanho="sm">
                    <span className="ui-rotulo inline-flex items-center gap-2"><CurrencyDollar size={16} weight="bold" aria-hidden="true" />AUC Total</span>
                    <span className="ui-kpi__valor">{formatCurrency(consolidated.pl_total)}</span>
                    <span className="ui-kpi__rotulo">
                      {consolidated.ref_label}
                      {consolidated.xp_carry_forward && (
                        <span className="ml-1 text-[color:var(--care-warning-text)]">· inclui XP de {aucData?.xp_latest?.ref_month ? formatMonth(aucData.xp_latest.ref_month) : 'upload anterior'}</span>
                      )}
                    </span>
                  </div>
                </div>

                <div className="ui-card" data-testid="auc-contas">
                  <div className="ui-kpi" data-tamanho="sm">
                    <span className="ui-rotulo inline-flex items-center gap-2"><UserCheck size={16} weight="bold" aria-hidden="true" />Total Contas</span>
                    <span className="ui-kpi__valor"><AnimNum value={consolidated.num_contas} /></span>
                    <span className="ui-kpi__rotulo flex gap-3">
                      <span>BTG: {consolidated.btg_contas}</span>
                      <span>XP: {consolidated.xp_contas}</span>
                    </span>
                  </div>
                </div>

                <div className="ui-card">
                  <div className="ui-kpi" data-tamanho="sm">
                    <span className="ui-rotulo inline-flex items-center gap-2"><Wallet size={16} weight="bold" aria-hidden="true" />Ticket Médio</span>
                    <span className="ui-kpi__valor">{formatCurrency(consolidated.ticket_medio)}</span>
                    <span className="ui-kpi__rotulo">por conta</span>
                  </div>
                </div>

                {/* Donut-like proportion card */}
                <div className="ui-card" data-testid="auc-proporcao">
                  <span className="ui-rotulo inline-flex items-center gap-2"><ChartBar size={16} weight="bold" aria-hidden="true" />Proporção</span>
                  {(() => {
                    const btgPl = consolidated.btg_pl || 0;
                    const xpPl = consolidated.xp_pl || 0;
                    const total = btgPl + xpPl;
                    const btgPerc = total > 0 ? (btgPl / total * 100) : 0;
                    const xpPerc = total > 0 ? (xpPl / total * 100) : 0;

                    return (
                      <div>
                        <div className="flex h-5 rounded-sm overflow-hidden bg-[var(--care-bg-secondary)] mb-2">
                          {btgPerc > 0 && (
                            <motion.div className="bg-[var(--care-chart-cat-1)] flex items-center justify-center" initial={{ width: 0 }} animate={{ width: `${btgPerc}%` }} transition={{ delay: 0, duration: 0.28, ease: [0.2, 0, 0, 1] }}>
                              {btgPerc > 15 && <span className="text-[11px] font-semibold text-[color:var(--care-accent-on-solid)] tabular-nums lining-nums">{btgPerc.toFixed(0)}%</span>}
                            </motion.div>
                          )}
                          {xpPerc > 0 && (
                            <motion.div className="bg-[var(--care-chart-cat-2)] flex items-center justify-center" initial={{ width: 0 }} animate={{ width: `${xpPerc}%` }} transition={{ delay: 0.04, duration: 0.28, ease: [0.2, 0, 0, 1] }}>
                              {xpPerc > 15 && <span className="text-[11px] font-semibold text-[color:var(--care-accent-on-solid)] tabular-nums lining-nums">{xpPerc.toFixed(0)}%</span>}
                            </motion.div>
                          )}
                        </div>
                        <div className="flex justify-between text-[11px] tabular-nums lining-nums">
                          <span className="text-[color:var(--care-text-secondary)] font-medium">BTG {fmtMilhoes(btgPl)}</span>
                          <span className="text-[color:var(--care-text-secondary)] font-medium">XP {fmtMilhoes(xpPl)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
              );
            })()}

            {/* === VARIAÇÃO DE PL (Δ no período capturado) — derivado da série diária real === */}
            {aucMonthly && aucMonthly.btgAsc.length > 1 && (() => {
              const sign = sinal; // zero sai sem sinal (DS v3 §2.5)
              const fmtM = (v) => `${sign(v)}R$ ${Math.abs(v / 1e6).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
              const dt = (s) => s ? new Date(s + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : '—';
              const mLabel = mLabelAuc;
              const { btgAsc, xpAsc, btgME, xpME, months } = aucMonthly;
              const rows = months.map((m, i) => {
                const prev = months[i - 1];
                const btgNow = btgME[m]?.pl_total || 0;
                const btgPrev = prev ? (btgME[prev]?.pl_total || 0) : (btgAsc[0]?.pl_total || 0);
                const xpNow = xpME[m]?.pl_total || 0;
                const xpPrev = prev ? (xpME[prev]?.pl_total ?? xpNow) : xpNow;
                return { m, btg: btgNow - btgPrev, xp: xpNow - xpPrev };
              });
              const btgFirst = btgAsc[0], btgLast = btgAsc[btgAsc.length - 1];
              const btgDelta = (btgLast?.pl_total || 0) - (btgFirst?.pl_total || 0);
              const btgContas = (btgLast?.num_contas || 0) - (btgFirst?.num_contas || 0);
              const xpNZ = xpAsc.filter(d => d.pl_total > 0);
              const xpFirst = xpNZ[0], xpLast = xpNZ[xpNZ.length - 1];
              const xpDelta = (xpFirst && xpLast) ? xpLast.pl_total - xpFirst.pl_total : null;
              const totalDelta = btgDelta + (xpDelta || 0);
              const denom = Math.abs(btgDelta) + Math.abs(xpDelta || 0);
              const btgShare = denom > 0 ? Math.abs(btgDelta) / denom * 100 : 100;
              const xpShare = 100 - btgShare;
              return (
                <div className="ui-card overflow-hidden mb-8" style={{ padding: 0, gap: 0 }} data-testid="auc-entrada">
                  <div className="ui-cabecalho px-5 py-4 border-b border-[color:var(--care-border-subtle)]">
                    <h3 className="ui-titulo" data-tamanho="sm">Variação de PL <span className="text-[color:var(--care-text-tertiary)] font-normal">(Δ no período)</span></h3>
                    <span className="ui-meta ui-cabecalho__acoes tabular-nums lining-nums">{dt(btgFirst?.ref_date)} → {dt(btgLast?.ref_date)}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[color:var(--care-border-subtle)]">
                    <div className="p-5">
                      <span className="ui-rotulo">BTG</span>
                      <p className={`text-3xl font-semibold tracking-[-0.01em] tabular-nums lining-nums mt-1 ${corVariacao(btgDelta)}`}>{fmtM(btgDelta)}</p>
                      <p className="ui-meta mt-1 tabular-nums lining-nums">{sign(btgContas)}{Math.abs(btgContas)} contas</p>
                    </div>
                    <div className="p-5">
                      <span className="ui-rotulo">XP</span>
                      <p className={`text-3xl font-semibold tracking-[-0.01em] tabular-nums lining-nums mt-1 ${xpDelta == null ? 'text-[color:var(--care-neutral-text)]' : corVariacao(xpDelta)}`}>{xpDelta != null ? fmtM(xpDelta) : '—'}</p>
                      <p className="ui-meta mt-1 tabular-nums lining-nums">{xpNZ.length > 1 ? `${dt(xpFirst?.ref_date)} → ${dt(xpLast?.ref_date)}` : 'série XP curta'}</p>
                    </div>
                    <div className="p-5">
                      <span className="ui-rotulo">Total</span>
                      <p className={`text-3xl font-semibold tracking-[-0.01em] tabular-nums lining-nums mt-1 ${corVariacao(totalDelta)}`}>{fmtM(totalDelta)}</p>
                      <div className="flex h-1.5 rounded-sm overflow-hidden bg-[var(--care-bg-secondary)] mt-2">
                        <div className="bg-[var(--care-chart-cat-1)]" style={{ width: `${btgShare}%` }} />
                        <div className="bg-[var(--care-chart-cat-3)]" style={{ width: `${xpShare}%` }} />
                      </div>
                      <p className="ui-meta mt-1 tabular-nums lining-nums">BTG {btgShare.toFixed(0)}% · XP {xpShare.toFixed(0)}%</p>
                    </div>
                  </div>
                  {rows.length > 0 && (
                    <div className="px-5 py-4 border-t border-[color:var(--care-border-subtle)]">
                      <table className="w-full text-xs tabular-nums lining-nums">
                        <thead>
                          <tr className="text-[13px] text-[color:var(--care-text-secondary)]">
                            <th scope="col" className="w-16 text-left font-semibold pb-2">Mês (Δ PL)</th>
                            <th scope="col" className="text-right font-semibold pb-2">BTG</th>
                            <th scope="col" className="text-right font-semibold pb-2">XP</th>
                            <th scope="col" className="text-right font-semibold pb-2">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.m}>
                              <th scope="row" className="text-left font-normal text-[color:var(--care-text-secondary)] py-1">{mLabel(r.m)}</th>
                              <td className={`text-right py-1 ${corVariacao(r.btg)}`}>{fmtM(r.btg)}</td>
                              <td className={`text-right py-1 ${!r.xp ? 'text-[color:var(--care-neutral-text)]' : corVariacao(r.xp)}`}>{r.xp ? fmtM(r.xp) : '—'}</td>
                              <td className={`text-right py-1 font-medium ${corVariacao(r.btg + r.xp)}`}>{fmtM(r.btg + r.xp)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="ui-meta mt-3">Δ PL = variação do patrimônio entre as datas (captação + rendimento). 1º mês conta desde o início da captura.</p>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Captação indisponível (fonte falhou) — erro discreto em vez de sumir/mostrar R$ 0 */}
            {captacaoErro && captacao.periodos.length === 0 && (
              <div className="ui-card items-start mb-8" data-testid="auc-captacao-erro">
                <h3 className="ui-titulo" data-tamanho="sm">Captação da Care — entrada de dinheiro novo</h3>
                <p role="alert" className="ui-aviso" data-tom="negativo">
                  <WarningCircle size={16} weight="bold" aria-hidden="true" />
                  Dados de captação indisponíveis no momento.
                </p>
                <button type="button" className="ui-botao" data-variante="fio" data-tamanho="sm" onClick={fetchCaptacao}>Tentar de novo</button>
              </div>
            )}

            {/* Captação da Care — STVM (caixa) + posições (custódia), sem dupla contagem */}
            {captacao.periodos.length > 0 && (
              <div className="ui-card mb-8" data-testid="auc-captacao">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="ui-pilha" data-gap="2">
                    <h3 className="ui-titulo" data-tamanho="sm">Captação da Care — entrada de dinheiro novo</h3>
                    <p className="ui-meta">
                      PL inicial de contas novas + captação STVM das contas existentes &middot; fonte BTG (STVM + posições) &middot;{' '}
                      <span className="text-[color:var(--care-text-secondary)]">≠ variação de PL de mercado</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* SELETOR mensal / anual */}
                    <div className="ui-segmentado" role="group" aria-label="Período da captação">
                      {['mensal', 'anual'].map((v) => (
                        <button key={v} type="button" className="ui-segmentado__opcao capitalize" aria-pressed={captacaoView === v} onClick={() => setCaptacaoView(v)}>
                          {v}
                        </button>
                      ))}
                    </div>
                    {captacaoView === 'mensal' && captacaoAnos.length > 0 && (
                      <select
                        value={captacaoAno}
                        onChange={(e) => setCaptacaoAno(parseInt(e.target.value, 10))}
                        className="ui-campo"
                        data-tamanho="sm"
                        aria-label="Ano da captação"
                      >
                        {captacaoAnos.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    )}
                    <div className="ui-kpi items-end" data-tamanho="sm">
                      <span className="ui-rotulo">
                        {captacaoView === 'anual' ? 'Total no período' : `Total ${captacaoAno}`}
                      </span>
                      <span className={`ui-kpi__valor ${corVariacao(captacao.totais.captacao_total || 0)}`}>
                        {fmtVar(captacao.totais.captacao_total || 0)}
                      </span>
                    </div>
                  </div>
                </div>
                {(() => {
                  const itens = captacao.periodos;
                  const maxMag = Math.max(...itens.map(m => Math.abs(m.captacao_total)), 1);
                  const H = 80;
                  const minW = Math.max(itens.length * 64, 300);
                  const abbr = (v) => {
                    const a = Math.abs(v);
                    const corpo = a >= 1000000 ? fmtMilhoes(a) : a >= 1000 ? `${(a / 1000).toFixed(0)}k` : `${a.toFixed(0)}`;
                    return `${sinal(v)}${corpo}`;
                  };
                  const NOMES = ['', 'jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
                  const label = (p) => (captacaoView === 'anual' ? p : (NOMES[parseInt(p.split('-')[1], 10)] || p));
                  return (
                    <div className="overflow-x-auto">
                      <div className="relative" style={{ minWidth: `${minW}px` }}>
                        <div className="flex items-stretch gap-3">
                          {itens.map((m, idx) => {
                            const barH = Math.max((Math.abs(m.captacao_total) / maxMag) * H, 3);
                            const pos = m.captacao_total >= 0;
                            const delay = idx * 0.02;
                            const tip = `${m.periodo}: ${fmtVar(m.captacao_total)}\n`
                              + `• contas novas (inclui custódia): ${formatCurrency(m.patrimonio_contas_novas)} (${m.contas_novas})\n`
                              + `• contas existentes (STVM caixa): ${formatCurrency(m.captacao_contas_existentes)}\n`
                              + `• STVM bruto: ${formatCurrency(m.stvm_liquido)} · câmbio: ${formatCurrency(m.cambio)}`;
                            return (
                              <div key={m.periodo} className="flex-1 flex flex-col items-center" title={tip}>
                                <div className="w-full flex flex-col justify-end items-center" style={{ height: `${H}px` }}>
                                  {pos && (
                                    <>
                                      <span className="text-[11px] font-medium text-[color:var(--care-chart-label)] mb-0.5 whitespace-nowrap tabular-nums lining-nums">{abbr(m.captacao_total)}</span>
                                      <motion.div className="w-full max-w-[44px] rounded-t-sm"
                                        style={{ height: `${barH}px`, backgroundColor: 'var(--care-chart-div-pos-2)', transformOrigin: 'bottom' }}
                                        initial={{ scaleY: 0 }} animate={{ scaleY: 1 }}
                                        transition={{ delay, duration: 0.28, ease: [0.2, 0, 0, 1] }} />
                                    </>
                                  )}
                                </div>
                                <div className="w-full border-t border-[color:var(--care-chart-axis)]" />
                                <div className="w-full flex flex-col justify-start items-center" style={{ height: `${H}px` }}>
                                  {!pos && (
                                    <>
                                      <motion.div className="w-full max-w-[44px] rounded-b-sm"
                                        style={{ height: `${barH}px`, backgroundColor: 'var(--care-chart-div-neg-2)', transformOrigin: 'top' }}
                                        initial={{ scaleY: 0 }} animate={{ scaleY: 1 }}
                                        transition={{ delay, duration: 0.28, ease: [0.2, 0, 0, 1] }} />
                                      <span className="text-[11px] font-medium text-[color:var(--care-chart-label)] mt-0.5 whitespace-nowrap tabular-nums lining-nums">{abbr(m.captacao_total)}</span>
                                    </>
                                  )}
                                </div>
                                <div className="text-[11px] text-[color:var(--care-chart-label)] mt-1 capitalize">{label(m.periodo)}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <div className="ui-legenda">
                  <span className="ui-legenda__item"><span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-div-pos-2)' }} />Captação positiva</span>
                  <span className="ui-legenda__item"><span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-div-neg-2)' }} />Saída líquida</span>
                  {captacao.totais.contas_novas > 0 && (
                    <span className="text-[color:var(--care-text-secondary)] tabular-nums lining-nums">{captacao.totais.contas_novas} contas novas no período</span>
                  )}
                </div>

                {/* A quebra de cada barra (antes só no title, fora do teclado e do toque) */}
                <details className="ui-detalhe">
                  <summary className="ui-detalhe__resumo">
                    <span className="ui-detalhe__chevron"><CaretRight size={16} weight="bold" aria-hidden="true" /></span>
                    Ver por {captacaoView === 'anual' ? 'ano' : 'mês'}
                  </summary>
                  <div className="overflow-x-auto mt-2">
                    <table className="w-full text-xs tabular-nums lining-nums">
                      <thead>
                        <tr className="text-[color:var(--care-text-secondary)]">
                          <th scope="col" className="text-left font-semibold pb-2 pr-3">Período</th>
                          <th scope="col" className="text-right font-semibold pb-2 px-3">Captação</th>
                          <th scope="col" className="text-right font-semibold pb-2 px-3">Contas novas (inclui custódia)</th>
                          <th scope="col" className="text-right font-semibold pb-2 px-3">Contas existentes (STVM caixa)</th>
                          <th scope="col" className="text-right font-semibold pb-2 px-3">STVM bruto</th>
                          <th scope="col" className="text-right font-semibold pb-2 pl-3">Câmbio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {captacao.periodos.map((m) => (
                          <tr key={m.periodo} className="border-t border-[color:var(--care-border-subtle)]">
                            <th scope="row" className="text-left font-normal py-1 pr-3 whitespace-nowrap text-[color:var(--care-text-secondary)]">{m.periodo}</th>
                            <td className={`text-right py-1 px-3 whitespace-nowrap ${corVariacao(m.captacao_total)}`}>{fmtVar(m.captacao_total)}</td>
                            <td className="text-right py-1 px-3 whitespace-nowrap">{formatCurrency(m.patrimonio_contas_novas)} ({m.contas_novas})</td>
                            <td className="text-right py-1 px-3 whitespace-nowrap">{formatCurrency(m.captacao_contas_existentes)}</td>
                            <td className="text-right py-1 px-3 whitespace-nowrap">{formatCurrency(m.stvm_liquido)}</td>
                            <td className="text-right py-1 pl-3 whitespace-nowrap">{formatCurrency(m.cambio)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>

                {/* Quebra por consultor */}
                {captacao.consultores.length > 0 && (() => {
                  const porConsultor = Object.values(
                    captacao.consultores.reduce((acc, c) => {
                      if (!acc[c.consultor]) acc[c.consultor] = { consultor: c.consultor, liquido: 0 };
                      acc[c.consultor].liquido += c.liquido;
                      return acc;
                    }, {})
                  ).sort((a, b) => b.liquido - a.liquido).slice(0, 8);
                  const maxC = Math.max(...porConsultor.map(c => Math.abs(c.liquido)), 1);
                  return (
                    <div className="pt-4 border-t border-[color:var(--care-border-subtle)]">
                      <h4 className="ui-rotulo mb-2">Captação por consultor (STVM, caixa)</h4>
                      <div className="space-y-1.5">
                        {porConsultor.map((c) => (
                          <div key={c.consultor} className="flex items-center gap-2">
                            <span className="text-[11px] text-[color:var(--care-text-secondary)] w-44 truncate" title={c.consultor}>{c.consultor}</span>
                            <div className="flex-1 bg-[var(--care-bg-secondary)] rounded-sm h-3 overflow-hidden">
                              <div className="h-full rounded-sm"
                                style={{ width: `${Math.max((Math.abs(c.liquido) / maxC) * 100, 1)}%`, backgroundColor: c.liquido >= 0 ? 'var(--care-chart-div-pos-2)' : 'var(--care-chart-div-neg-2)' }} />
                            </div>
                            <span className="text-[11px] tabular-nums lining-nums text-[color:var(--care-text-secondary)] w-28 text-right">{fmtVar(c.liquido)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Gráfico Evolução AUC — mensal (derivado da série diária real: fim de cada mês) */}
            {aucMonthly && (() => {
              const mLabel = mLabelAuc;
              const { btgME, xpME, months } = aucMonthly;
              const series = months.map((m) => ({
                ref_month: m,
                btg: btgME[m]?.pl_total || 0,
                xp: xpME[m]?.pl_total || 0,
              }));
              const maxTotal = Math.max(1, ...series.map(s => s.btg + s.xp));
              const mi = maxTotal / 1e6;
              let yMax; if (mi<=10) yMax=10; else if(mi<=25) yMax=25; else if(mi<=50) yMax=50; else if(mi<=75) yMax=75; else if(mi<=100) yMax=100; else if(mi<=150) yMax=150; else if(mi<=200) yMax=200; else yMax=Math.ceil(mi/50)*50;
              const yv = yMax * 1e6;
              return (
                <div className="ui-card mb-8" data-testid="auc-mensal">
                  <div className="ui-cabecalho">
                    <h3 className="ui-titulo" data-tamanho="sm">Evolução do AUC — mensal</h3>
                    <span className="ui-meta ui-cabecalho__acoes tabular-nums lining-nums">{series.length} {series.length === 1 ? 'mês' : 'meses'}</span>
                  </div>
                  <div className="relative">
                    <div className="absolute left-0 top-0 bottom-8 w-12 flex flex-col justify-between text-[11px] text-[color:var(--care-chart-label)] text-right pr-2 tabular-nums lining-nums">
                      <span>{yMax}M</span><span>{yMax/2}M</span><span>0</span>
                    </div>
                    <div className="ml-14">
                      <div className="absolute left-14 right-0 top-0 h-[200px] flex flex-col justify-between pointer-events-none">
                        <div className="border-t border-[color:var(--care-chart-grid)] w-full"></div>
                        <div className="border-t border-[color:var(--care-chart-grid)] w-full"></div>
                        <div className="border-t border-[color:var(--care-chart-axis)] w-full"></div>
                      </div>
                      <div className="flex items-end gap-4 h-[200px] relative">
                        {series.map((s, idx) => {
                          const total = s.btg + s.xp;
                          const btgH = (s.btg / yv) * 200;
                          const xpH = (s.xp / yv) * 200;
                          const delay = idx * 0.02;
                          const tooltipAlign = idx < series.length / 2 ? 'left-0' : 'right-0';
                          const prev = idx > 0 ? series[idx-1] : null;
                          const dTot = prev ? total - (prev.btg + prev.xp) : null;
                          return (
                            <div
                              key={s.ref_month}
                              className="flex-1 flex flex-col items-center justify-end min-w-[44px] h-full group relative rounded-sm"
                              tabIndex={0}
                              role="img"
                              aria-label={`${mLabel(s.ref_month)}: BTG ${formatCurrency(s.btg)}, XP ${formatCurrency(s.xp)}, total ${formatCurrency(total)}${dTot !== null ? `, Δ mês ${fmtVar(dTot)}` : ''}`}
                            >
                              <div aria-hidden="true" className={`pointer-events-none absolute bottom-full mb-2 ${tooltipAlign} hidden group-hover:block group-focus-visible:block z-dropdown bg-[var(--care-bg-inverse)] text-[color:var(--care-text-inverse)] text-[11px] rounded-md [box-shadow:var(--care-shadow-lg)] px-3 py-2 whitespace-nowrap tabular-nums lining-nums`}>
                                <div className="font-medium text-[color:var(--care-text-inverse)] mb-1">{mLabel(s.ref_month)}</div>
                                <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-sm bg-[var(--care-chart-cat-1)]"></span><span className="text-[color:color-mix(in_srgb,var(--care-text-inverse)_70%,transparent)]">BTG:</span><span className="font-medium">{formatCurrency(s.btg)}</span></div>
                                <div className="flex items-center gap-2 mt-0.5"><span className="w-2 h-2 rounded-sm bg-[var(--care-chart-cat-3)]"></span><span className="text-[color:color-mix(in_srgb,var(--care-text-inverse)_70%,transparent)]">XP:</span><span className="font-medium">{formatCurrency(s.xp)}</span></div>
                                <div className="mt-1 pt-1 border-t border-[color:color-mix(in_srgb,var(--care-text-inverse)_20%,transparent)] text-[color:color-mix(in_srgb,var(--care-text-inverse)_70%,transparent)]">Total: <span className="font-medium text-[color:var(--care-text-inverse)]">{formatCurrency(total)}</span></div>
                                {dTot !== null && (<div className="mt-0.5 text-[color:color-mix(in_srgb,var(--care-text-inverse)_70%,transparent)]">Δ mês: <span className="font-medium text-[color:var(--care-text-inverse)]">{fmtVar(dTot)}</span></div>)}
                              </div>
                              <div className="absolute inset-0 cursor-help"></div>
                              <span className="text-[11px] font-semibold mb-1 text-[color:var(--care-chart-label)] tabular-nums lining-nums">{fmtMilhoes(total)}</span>
                              <div className="w-full max-w-[44px] flex flex-col-reverse rounded-t-sm overflow-hidden" style={{ transformOrigin: 'bottom' }}>
                                {s.btg > 0 && (<motion.div className="bg-[var(--care-chart-cat-1)] group-hover:brightness-90 transition-[filter] duration-fast ease-care" style={{ height: `${btgH}px`, minHeight: '4px' }} initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay, duration: 0.28, ease: [0.2, 0, 0, 1] }} />)}
                                {s.xp > 0 && (<motion.div className="bg-[var(--care-chart-cat-3)] group-hover:brightness-90 transition-[filter] duration-fast ease-care" style={{ height: `${xpH}px`, minHeight: '4px' }} initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay: delay+0.05, duration: 0.28, ease: [0.2, 0, 0, 1] }} />)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex gap-4 mt-3">
                        {series.map((s) => (<div key={s.ref_month} className="flex-1 text-center text-[11px] text-[color:var(--care-chart-label)] min-w-[44px]">{mLabel(s.ref_month)}</div>))}
                      </div>
                    </div>
                  </div>
                  <div className="ui-legenda justify-center">
                    <span className="ui-legenda__item"><span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-cat-1)' }} />BTG</span>
                    <span className="ui-legenda__item"><span className="ui-legenda__amostra" style={{ background: 'var(--care-chart-cat-3)' }} />XP</span>
                  </div>
                </div>
              );
            })()}


            {/* Histórico AUC — mensal (derivado da série diária: fim de cada mês) */}
            {aucMonthly && (() => {
              const mLabel = mLabelAuc;
              const { btgAsc, btgME, xpME, months } = aucMonthly;
              const rows = months.map((m, i) => {
                const prev = months[i-1];
                const btg = btgME[m]?.pl_total || 0;
                const xp = xpME[m]?.pl_total || 0;
                const contas = (btgME[m]?.num_contas || 0) + (xpME[m]?.num_contas || 0);
                const prevTot = prev ? ((btgME[prev]?.pl_total||0)+(xpME[prev]?.pl_total||0)) : (btgAsc[0]?.pl_total||0);
                return { m, btg, xp, total: btg+xp, contas, delta: (btg+xp) - prevTot, hasBtg: !!btgME[m], hasXp: !!xpME[m] };
              }).reverse();
              return (
                <div className="ui-card overflow-hidden" style={{ padding: 0, gap: 0 }} data-testid="auc-historico">
                  <div className="ui-cabecalho px-5 py-4 border-b border-[color:var(--care-border-subtle)]">
                    <h3 className="ui-titulo" data-tamanho="sm">Histórico AUC — mensal</h3>
                    <span className="ui-meta ui-cabecalho__acoes">fim de cada mês · Δ vs mês anterior</span>
                  </div>
                  <div className="divide-y divide-[color:var(--care-border-subtle)]">
                    {rows.map((r) => (
                      <div key={r.m} className="flex items-center justify-between px-5 py-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-[15px] font-medium text-[color:var(--care-text-secondary)] w-14">{mLabel(r.m)}</span>
                          <div className="hidden sm:flex gap-1">
                            {r.hasBtg && <span className="ui-pilula">BTG</span>}
                            {r.hasXp && <span className="ui-pilula">XP</span>}
                          </div>
                          <span className={`text-[11px] tabular-nums lining-nums font-medium ${corVariacao(r.delta)}`}>{fmtVar(r.delta)}</span>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right"><p className="text-[15px] font-medium text-[color:var(--care-text-primary)] tabular-nums lining-nums">{formatCurrency(r.total)}</p><p className="ui-meta">AUC Total</p></div>
                          <div className="text-right hidden sm:block"><p className="text-xs text-[color:var(--care-text-secondary)] tabular-nums lining-nums">{formatCurrency(r.btg)}</p><p className="ui-meta">BTG</p></div>
                          <div className="text-right hidden sm:block"><p className="text-xs text-[color:var(--care-text-secondary)] tabular-nums lining-nums">{r.xp ? formatCurrency(r.xp) : '—'}</p><p className="ui-meta">XP</p></div>
                          <div className="text-right"><p className="text-[15px] font-medium text-[color:var(--care-text-primary)] tabular-nums lining-nums">{r.contas}</p><p className="ui-meta">Contas</p></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Upload XP — utilitário no fim da aba (BTG vem automático pela API; XP é manual) */}
            <div className="mt-8 pt-6 border-t border-[color:var(--care-border-subtle)]" data-testid="upload-xp">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="ui-pilha" data-gap="2">
                  <h3 className="ui-titulo" data-tamanho="sm">Atualizar XP</h3>
                  <p className="ui-meta">
                    O BTG é automático pela API. O XP entra por upload manual do Excel (.xlsx).
                    {aucData?.xp_latest?.ref_month && <span> · último: {formatMonth(aucData.xp_latest.ref_month)}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div role="status" aria-live="polite">
                    {uploadMsgXp && (
                      <p className="ui-aviso" data-tom={uploadMsgXp.type === 'success' ? 'positivo' : 'negativo'}>
                        {uploadMsgXp.type === 'success'
                          ? <CheckCircle size={16} weight="bold" aria-hidden="true" />
                          : <WarningCircle size={16} weight="bold" aria-hidden="true" />}
                        {uploadMsgXp.text}
                      </p>
                    )}
                  </div>
                  {/* O input fica sr-only (não display:none) para continuar no Tab; o anel aparece na label. */}
                  <label
                    className="ui-botao has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-[color:var(--care-border-focus)]"
                    data-variante="fio"
                    data-tamanho="sm"
                    aria-disabled={uploadingXp || undefined}
                  >
                    <UploadSimple size={16} weight="bold" aria-hidden="true" className={uploadingXp ? 'motion-safe:animate-pulse' : undefined} />
                    {uploadingXp ? 'Processando...' : 'Excel XP (.xlsx)'}
                    <input type="file" accept=".xlsx,.xls" onChange={handleUploadXp} className="sr-only" disabled={uploadingXp} />
                  </label>
                </div>
              </div>
            </div>

            </>
            )}
          </>
        )}

        {/* PROJETOS TAB */}
        {activeTab === 'projetos' && (
          <ErrorBoundary>
            <React.Suspense fallback={<TabSkeleton label="Carregando Projetos..." />}>
              <ProjetosTab backendUrl={backendUrl} isAdmin={ehAdminPainel(user)} />
            </React.Suspense>
          </ErrorBoundary>
        )}

        {/* PIPELINE TAB */}
        {activeTab === 'pipeline' && (
          <ErrorBoundary>
            <React.Suspense fallback={<TabSkeleton label="Carregando Pipeline..." />}>
              <PipelineTab selectedYear={selectedYear || new Date().getFullYear()} backendUrl={backendUrl} isAdmin={ehAdminPainel(user)} />
            </React.Suspense>
          </ErrorBoundary>
        )}

        {/* REUNIOES TAB */}
        {activeTab === 'reunioes' && (
          <ErrorBoundary>
            <React.Suspense fallback={<TabSkeleton label="Carregando Reuniões..." />}>
              <ReunioesTab />
            </React.Suspense>
          </ErrorBoundary>
        )}

        {/* PÓS-REUNIÃO (cobrança de docs) */}
        {activeTab === 'pos-reuniao' && (
          <PostReuniaoTab />
        )}

        {/* RECEITAS TAB */}
        {activeTab === 'receitas' && (
          <ErrorBoundary>
            <React.Suspense fallback={<TabSkeleton label="Carregando Receitas..." />}>
              <ReceitasTab />
            </React.Suspense>
          </ErrorBoundary>
        )}

        {/* VISÃO CONSOLIDADA (só admin do painel; o backend também exige) */}
        {activeTab === 'visao-consolidada' && ehAdminPainel(user) && (
          <ErrorBoundary>
            <React.Suspense fallback={<TabSkeleton label="Carregando Visão consolidada..." />}>
              <VisaoConsolidadaTab backendUrl={backendUrl} />
            </React.Suspense>
          </ErrorBoundary>
        )}

        {activeTab === 'admin' && (
          <ErrorBoundary>
            <React.Suspense fallback={<TabSkeleton label="Carregando Admin..." />}>
              <AdminTab backendUrl={backendUrl} />
            </React.Suspense>
          </ErrorBoundary>
        )}
        </main>

        {/* Footer */}
        {lastUpdate && (
          <div className="mt-8 text-center">
            <p className="ui-meta tabular-nums lining-nums">
              Atualizado em {lastUpdate.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
            </p>
          </div>
        )}

        {/* Modal de confirmação — apagar lead */}
        {leadToDelete && (
          <div className="fixed inset-0 z-modal flex items-center justify-center bg-[var(--care-bg-overlay)] p-4" onClick={() => !deletingLead && setLeadToDelete(null)}>
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="apagar-lead-titulo"
              aria-describedby="apagar-lead-texto"
              className="bg-[var(--care-bg-surface-raised)] rounded-lg border border-[color:var(--care-border-subtle)] [box-shadow:var(--care-shadow-lg)] max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => { if (e.key === 'Escape' && !deletingLead) setLeadToDelete(null); }}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-[var(--care-danger-bg)] text-[color:var(--care-danger-text)] grid place-items-center shrink-0" aria-hidden="true">
                  <Trash size={20} aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h3 id="apagar-lead-titulo" className="ui-titulo">Apagar lead?</h3>
                  <p id="apagar-lead-texto" className="text-[15px] text-[color:var(--care-text-secondary)] mt-1">
                    Tem certeza que deseja apagar <b className="text-[color:var(--care-text-primary)]">{leadToDelete.nome || 'este lead'}</b>? Esta ação não pode ser desfeita.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button type="button" className="ui-botao" data-variante="fio" autoFocus onClick={() => setLeadToDelete(null)} disabled={deletingLead}>Cancelar</button>
                <button type="button" className="ui-botao" data-variante="fio" data-tom="negativo" onClick={() => apagarLead(leadToDelete)} disabled={deletingLead} aria-busy={deletingLead || undefined}>
                  {deletingLead ? 'Apagando…' : (<><Trash size={16} weight="bold" aria-hidden="true" /> Apagar</>)}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </MotionConfig>
  );
};

export default TypeformDashboard;
